import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import JSZip from 'jszip';
import { ApiService } from '../../core/services/api.service';
import { jsPDF } from 'jspdf';
import { FeedbackService } from '../../core/services/feedback.service';
import { DashboardService } from '../../core/services/dashboard.service';
import { environment } from '../../../environments/environment';
import Swal from 'sweetalert2';
import * as _mqtt from 'mqtt';

const mqtt = (_mqtt as any).default || _mqtt;

import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-order-detail',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './order-detail.component.html',
  styleUrl: './order-detail.component.scss',
})
export class OrderDetailComponent implements OnInit, OnDestroy {
  sessionData: any = null;
  isLoading = true;
  isCheckoutLoading = false;
  isTransactionModalOpen = false;
  isCheckoutModalOpen = false;
  purchaseMode: 'single' | 'all' = 'single';
  selectedFile: any = null;
  transactionData: any = null;
  checkoutError = '';
  isAllFilesUnlocked = false;
  lastZeusOrderId: any = null;

  // Download progress state
  isDownloading = false;
  downloadProgress = 0;
  downloadCompletedCount = 0;
  downloadTotalCount = 0;
  downloadStatusText = '';
  
  videoLoadedState: Record<string, boolean> = {};

  // Modal State Properties
  isModalOpen = false;
  selectedPhotoUrl = '';
  selectedPhotoName = '';

  // Framed Modal State Properties (always unlocked)
  isFramedModalOpen = false;
  selectedFramedUrl = '';
  selectedFramedName = '';

  // Claim Limit Modal State Properties
  isClaimLimitModalOpen = false;
  selectedRedeemFiles: any[] = [];

  // Review Modal State
  isReviewModalOpen = false;
  reviewTargetSession: any = null;
  reviewRating = 0;
  reviewNote = '';
  isSubmittingReview = false;

  // Fotoin Aja Matches
  fotoinAjaMatches: any[] = [];
  isFotoinAjaLoading = false;

  // FAQ Accordion State
  activeFaqIndex: number | null = null;

  get softFiles(): any[] {
    if (!this.sessionData) return [];
    let photos: any[] = [];

    const addPhoto = (f: any, prefix: string) => {
      if (!f) return;
      const fileUrl =
        typeof f === 'string'
          ? f
          : f.download_url ||
            f.original_url ||
            f.url ||
            f.file_url ||
            f.path ||
            '';
      if (fileUrl && !photos.some((p) => p.url === fileUrl)) {
        const rawObj = typeof f === 'object' ? f : {};
        const backendId = this.getFileIntegerId(rawObj);

        photos.push({
          ...rawObj,
          backend_id: backendId,
          media_id: backendId ?? rawObj.media_id,
          photo_id: backendId ?? rawObj.photo_id,
          id: backendId ?? rawObj.id ?? `${prefix}_${photos.length + 1}`,
          synthetic_id: `${prefix}_${photos.length + 1}`,
          url: fileUrl,
          download_url: fileUrl,
          name: rawObj.name || `soft_file_${photos.length + 1}.jpg`,
          is_redeemed:
            rawObj.is_redeemed !== undefined ? rawObj.is_redeemed : true,
        });
      }
    };

    // 1. Root level soft_files
    if (Array.isArray(this.sessionData.soft_files)) {
      this.sessionData.soft_files.forEach((f: any) => addPhoto(f, 'sf_root'));
    }

    // 2. Order level soft_files
    if (Array.isArray(this.sessionData.order?.soft_files)) {
      this.sessionData.order.soft_files.forEach((f: any) =>
        addPhoto(f, 'sf_order'),
      );
    }

    // 3. Sessions level soft_files
    if (Array.isArray(this.sessionData.sessions)) {
      this.sessionData.sessions.forEach((session: any) => {
        if (Array.isArray(session.soft_files)) {
          session.soft_files.forEach((f: any) => addPhoto(f, 'sf_session'));
        }
      });
    }

    // Sort: Unlocked / authorized photos first (terbuka di paling atas), diikuti foto yang masih terkunci
    return photos.sort((a, b) => {
      const aAuth = this.isFileAuthorized(a) ? 1 : 0;
      const bAuth = this.isFileAuthorized(b) ? 1 : 0;
      if (aAuth !== bAuth) {
        return bAuth - aAuth; // Unlocked (1) first, locked (0) after
      }
      return 0;
    });
  }

  get aiPhotos(): any[] {
    if (!this.sessionData) return [];
    let photos: any[] = [];

    const addPhoto = (f: any, prefix: string) => {
      if (!f) return;
      const fileUrl =
        typeof f === 'string'
          ? f
          : f.download_url ||
            f.original_url ||
            f.url ||
            f.file_url ||
            f.path ||
            '';
      if (fileUrl && !photos.some((p) => p.url === fileUrl)) {
        const rawObj = typeof f === 'object' ? f : {};
        const backendId = this.getFileIntegerId(rawObj);

        photos.push({
          ...rawObj,
          backend_id: backendId,
          media_id: backendId ?? rawObj.media_id,
          photo_id: backendId ?? rawObj.photo_id,
          id: backendId ?? rawObj.id ?? `${prefix}_${photos.length + 1}`,
          synthetic_id: `${prefix}_${photos.length + 1}`,
          url: fileUrl,
          download_url: fileUrl,
          name: rawObj.name || `ai_photo_${photos.length + 1}.jpg`,
          is_redeemed: true,
          is_ai: true,
        });
      }
    };

    if (Array.isArray(this.sessionData.ai_photos)) {
      this.sessionData.ai_photos.forEach((f: any) => addPhoto(f, 'ai_root'));
    }

    if (Array.isArray(this.sessionData.order?.ai_photos)) {
      this.sessionData.order.ai_photos.forEach((f: any) =>
        addPhoto(f, 'ai_order'),
      );
    }

    if (Array.isArray(this.sessionData.sessions)) {
      this.sessionData.sessions.forEach((session: any) => {
        if (Array.isArray(session.ai_photos)) {
          session.ai_photos.forEach((f: any) => addPhoto(f, 'ai_session'));
        }
      });
    }

    return photos;
  }

  get framedPhotos(): any[] {
    if (!this.sessionData) return [];
    let photos: any[] = [];

    const addPhoto = (f: any, prefix: string) => {
      if (!f) return;
      const fileUrl =
        typeof f === 'string'
          ? f
          : f.url ||
            f.download_url ||
            f.original_url ||
            f.file_url ||
            f.path ||
            f.image_url ||
            '';
      if (fileUrl && !photos.some((p) => p.url === fileUrl)) {
        photos.push({
          ...(typeof f === 'object' ? f : {}),
          id: `${prefix}_${photos.length + 1}`,
          url: fileUrl,
          download_url: fileUrl,
          name:
            (typeof f === 'object' && f.name) ||
            `framed_photo_${photos.length + 1}.jpg`,
        });
      }
    };

    // 1. Root level framed_photos or framed_photo
    if (Array.isArray(this.sessionData.framed_photos)) {
      this.sessionData.framed_photos.forEach((f: any) =>
        addPhoto(f, 'framed_root'),
      );
    }
    if (this.sessionData.framed_photo) {
      addPhoto(this.sessionData.framed_photo, 'framed_root_single');
    }

    // 2. Order level framed_photos
    if (Array.isArray(this.sessionData.order?.framed_photos)) {
      this.sessionData.order.framed_photos.forEach((f: any) =>
        addPhoto(f, 'framed_order'),
      );
    }

    // 3. Sessions level framed_photos or framed_photo
    if (Array.isArray(this.sessionData.sessions)) {
      this.sessionData.sessions.forEach((session: any) => {
        if (Array.isArray(session.framed_photos)) {
          session.framed_photos.forEach((f: any) =>
            addPhoto(f, 'framed_session'),
          );
        }
        if (session.framed_photo) {
          addPhoto(session.framed_photo, 'framed_session_single');
        }
      });
    }

    return photos;
  }

  iosVideoSafeUrls: Record<string, any> = {};
  iosVideoLoading: Record<string, boolean> = {};
  private sanitizer = inject(DomSanitizer);

  get videoClips(): any[] {
    if (!this.sessionData) return [];
    let clips: any[] = [];

    const addClip = (f: any, prefix: string) => {
      if (!f) return;
      const fileUrl = typeof f === 'string' ? f : f.url || f.download_url || f.file_url || f.path || '';
      if (fileUrl && !clips.some((p) => p.url === fileUrl)) {
        const id = `${prefix}_${clips.length + 1}`;
        clips.push({
          ...(typeof f === 'object' ? f : {}),
          id: id,
          url: fileUrl,
          download_url: fileUrl,
          name: (typeof f === 'object' && f.name) || `video_clip_${clips.length + 1}.mp4`,
        });

        // Background download for iOS safely using dedicated video fetcher
        if (this.isIosDevice() && this.iosVideoLoading[id] === undefined && this.iosVideoSafeUrls[id] === undefined) {
          this.iosVideoLoading[id] = true;
          this.fetchVideoBlob(fileUrl).then(blob => {
            const blobUrl = window.URL.createObjectURL(blob);
            this.iosVideoSafeUrls[id] = this.sanitizer.bypassSecurityTrustUrl(blobUrl);
            this.iosVideoLoading[id] = false;
          }).catch(err => {
            console.error('Failed to load iOS video blob', err);
            this.iosVideoLoading[id] = false;
          });
        }
      }
    };

    if (Array.isArray(this.sessionData.video_clips)) {
      this.sessionData.video_clips.forEach((f: any) => addClip(f, 'video_root'));
    }
    if (Array.isArray(this.sessionData.order?.video_clips)) {
      this.sessionData.order.video_clips.forEach((f: any) => addClip(f, 'video_order'));
    }
    if (Array.isArray(this.sessionData.sessions)) {
      this.sessionData.sessions.forEach((session: any) => {
        if (Array.isArray(session.video_clips)) {
          session.video_clips.forEach((f: any) => addClip(f, 'video_session'));
        }
      });
    }

    return clips;
  }

  get device(): any {
    if (
      !this.sessionData ||
      !this.sessionData.sessions ||
      this.sessionData.sessions.length === 0
    )
      return null;
    return this.sessionData.sessions[0].device;
  }

  get isRedeemed(): boolean {
    if (this.isAllFilesUnlocked) return true;
    if (!this.sessionData) return false;
    // Check order level first
    if (
      this.sessionData.order &&
      this.sessionData.order.is_redeemed !== undefined
    ) {
      return this.sessionData.order.is_redeemed;
    }
    // Fallback to checking soft_files inside sessions
    if (this.sessionData.sessions && this.sessionData.sessions.length > 0) {
      return this.sessionData.sessions.some((s: any) => {
        if (s.is_redeemed !== undefined) return s.is_redeemed;
        if (s.soft_files && s.soft_files.length > 0) {
          return s.soft_files.some((f: any) => f.is_redeemed === true);
        }
        return false;
      });
    }
    return false;
  }

  get order(): any {
    return this.sessionData?.order || {};
  }

  get transactionTime(): string | null {
    return (
      this.order?.paid_at ||
      this.order?.created_at ||
      this.transactionData?.created_at ||
      this.sessionData?.created_at ||
      this.sessionData?.order?.created_at ||
      this.sessionData?.order_detail?.created_at ||
      this.sessionData?.sessions?.[0]?.order_detail?.created_at ||
      this.sessionData?.sessions?.[0]?.created_at ||
      null
    );
  }

  get softFilePrice(): number {
    return Number(this.order?.soft_file_price ?? 5000);
  }

  get allFilesPrice(): number {
    if (this.addonPackages && this.addonPackages.length > 0) {
      const price = Number(this.addonPackages[0].price);
      if (!isNaN(price) && price > 0) {
        return price;
      }
    }
    return Number(this.order?.all_soft_files_price ?? 50000);
  }

  get authorizedSoftFiles(): any[] {
    return this.softFiles.filter((file) => this.isFileAuthorized(file));
  }

  localAddonTransactions: any[] = [];
  pendingPurchasedPackage: any = null;

  get purchasedItems(): any[] {
    if (!this.sessionData) return [];
    const itemsMap = new Map<string, any>();

    const normalizeItem = (item: any) => {
      if (!item) return null;
      const rawProductName =
        item.details?.product_name ||
        item.details?.name ||
        item.product_name ||
        item.name ||
        item.details?.addon_name ||
        item.addon_name ||
        'Paket Foto';
      const productName = this.formatProductName(rawProductName);

      const sku =
        item.product_sku ||
        item.details?.product_sku ||
        item.details?.addon_sku ||
        item.sku ||
        item.details?.sku ||
        null;

      const price = Number(
        item.product_price ??
          item.details?.product_price ??
          item.details?.addon_price ??
          item.details?.price ??
          item.price ??
          item.sub_total ??
          item.total ??
          0,
      );

      const discount = Number(
        item.discount ??
          item.discount_source_details?.voucher_discount ??
          item.discount_source_details?.promo_discount ??
          0,
      );

      const qty = Number(item.qty ?? item.quantity ?? item.details?.qty ?? 1);

      const subtotal = Number(
        item.sub_total ??
          item.details?.sub_total ??
          (item.product_price ? Number(item.product_price) * qty : null) ??
          (item.details?.product_price
            ? Number(item.details.product_price) * qty
            : null) ??
          (price ? price * qty : null) ??
          item.total ??
          price,
      );

      const total = Number(
        item.total ?? item.details?.total ?? Math.max(0, subtotal - discount),
      );

      return {
        ...item,
        product_name: productName,
        name: productName,
        product_sku: sku,
        product_price: price,
        sub_total: subtotal,
        discount: discount,
        qty: qty,
        total: total,
        created_at:
          item.created_at ||
          item.details?.created_at ||
          item.order_detail?.created_at ||
          item.order?.created_at ||
          null,
      };
    };

    // 1. Check order.order_details (array)
    if (Array.isArray(this.sessionData.order?.order_details)) {
      this.sessionData.order.order_details.forEach((item: any) => {
        const key = String(
          item.id ||
            item.product_name ||
            item.details?.product_name ||
            itemsMap.size,
        );
        if (!itemsMap.has(key)) {
          const norm = normalizeItem(item);
          if (norm) itemsMap.set(key, norm);
        }
      });
    }

    // 2. Check order.order_detail (single object)
    if (this.sessionData.order?.order_detail) {
      const item = this.sessionData.order.order_detail;
      const key = String(
        item.id ||
          item.product_name ||
          item.details?.product_name ||
          itemsMap.size,
      );
      if (!itemsMap.has(key)) {
        const norm = normalizeItem(item);
        if (norm) itemsMap.set(key, norm);
      }
    }

    // 3. Check order_details (root array)
    if (Array.isArray(this.sessionData.order_details)) {
      this.sessionData.order_details.forEach((item: any) => {
        const key = String(
          item.id ||
            item.product_name ||
            item.details?.product_name ||
            itemsMap.size,
        );
        if (!itemsMap.has(key)) {
          const norm = normalizeItem(item);
          if (norm) itemsMap.set(key, norm);
        }
      });
    }

    // 4. Check sessions list for order_detail
    if (Array.isArray(this.sessionData.sessions)) {
      this.sessionData.sessions.forEach((session: any) => {
        if (session.order_detail) {
          const item = session.order_detail;
          const key = String(
            item.id ||
              item.product_name ||
              item.details?.product_name ||
              itemsMap.size,
          );
          if (!itemsMap.has(key)) {
            const norm = normalizeItem(item);
            if (norm) itemsMap.set(key, norm);
          }
        }
      });
    }

    return Array.from(itemsMap.values());
  }

  get purchasedAddonItems(): any[] {
    const addonsMap = new Map<string, any>();

    const addAddon = (item: any, source: string) => {
      if (!item) return;

      const rawProductName =
        item.details?.addon_name ||
        item.details?.product_name ||
        item.details?.name ||
        item.details?.title ||
        item.product_addon?.name ||
        item.product_addon?.product_addon_name ||
        item.product_addon_name ||
        item.addon_name ||
        item.product_name ||
        item.name ||
        item.title ||
        item.package_name ||
        'Paket Add-on';
      const productName = this.formatProductName(rawProductName);

      const isAutoRedeem =
        /auto-?redeem.*soft.*file/i.test(rawProductName) ||
        item.details?.source === 'multi_device_order_creation' ||
        (String(rawProductName).toLowerCase().includes('auto-redeem') &&
          /soft.*file/i.test(String(rawProductName)));

      const id = isAutoRedeem
        ? 'system_auto_redeem_soft_files'
        : item.id ||
          item.invoice ||
          item.reference_id ||
          item.transaction_id ||
          `addon_${addonsMap.size}`;
      const key = String(id);
      if (!addonsMap.has(key)) {

        const price = Number(
          item.details?.addon_price ??
            item.details?.product_price ??
            item.product_price ??
            item.product_addon?.price ??
            item.details?.price ??
            item.price ??
            item.sub_total ??
            item.total ??
            0,
        );

        const discount = Number(
          item.discount ??
            item.discount_source_details?.voucher_discount ??
            item.discount_source_details?.promo_discount ??
            0,
        );

        const qty = Number(item.qty ?? item.quantity ?? item.details?.qty ?? 1);

        const subtotal = Number(
          item.sub_total ??
            item.details?.sub_total ??
            (item.details?.addon_price
              ? Number(item.details.addon_price) * qty
              : null) ??
            (item.product_price ? Number(item.product_price) * qty : null) ??
            (price ? price * qty : null) ??
            item.total ??
            price,
        );

        const total = Number(
          item.total ?? item.details?.total ?? Math.max(0, subtotal - discount),
        );

        const sku =
          item.details?.addon_sku ||
          item.details?.product_sku ||
          item.product_sku ||
          item.details?.sku ||
          item.product_addon?.sku ||
          item.product_addon?.product_addon_sku ||
          null;

        addonsMap.set(key, {
          id: id,
          invoice:
            item.invoice ||
            item.order?.invoice ||
            item.order_invoice ||
            item.order_number ||
            item.reference_id ||
            item.transaction_id ||
            null,
          product_name: productName,
          name: productName,
          qty: qty,
          product_sku: sku,
          product_price: price,
          sub_total: subtotal,
          discount: discount,
          voucher_name:
            item.voucher_name ||
            item.discount_source_details?.voucher_name ||
            null,
          promo_name:
            item.promo_name || item.discount_source_details?.promo_name || null,
          total: total,
          status: item.status || item.order?.status || 'Paid',
          created_at:
            item.created_at || item.updated_at || item.paid_at || null,
          details: item.details || null,
          is_addon: true,
          source: source,
        });
      }
    };

    // 1. SessionData root order_addons
    if (Array.isArray(this.sessionData?.order_addons)) {
      this.sessionData.order_addons.forEach((item: any) =>
        addAddon(item, 'root_order_addons'),
      );
    }

    // 2. Order level order_addons
    if (Array.isArray(this.sessionData?.order?.order_addons)) {
      this.sessionData.order.order_addons.forEach((item: any) =>
        addAddon(item, 'order_order_addons'),
      );
    }

    // 3. Order level addons
    if (Array.isArray(this.sessionData?.order?.addons)) {
      this.sessionData.order.addons.forEach((item: any) =>
        addAddon(item, 'order_addons'),
      );
    }

    // 4. Root level addons
    if (Array.isArray(this.sessionData?.addons)) {
      this.sessionData.addons.forEach((item: any) =>
        addAddon(item, 'root_addons'),
      );
    }

    // 5. Sessions level order_addons and addons
    if (Array.isArray(this.sessionData?.sessions)) {
      this.sessionData.sessions.forEach((s: any) => {
        if (Array.isArray(s.order_addons)) {
          s.order_addons.forEach((item: any) =>
            addAddon(item, 'session_order_addons'),
          );
        }
        if (Array.isArray(s.addons)) {
          s.addons.forEach((item: any) => addAddon(item, 'session_addons'));
        }
      });
    }

    // 6. Root addon_orders / addon_transactions
    if (Array.isArray(this.sessionData?.addon_orders)) {
      this.sessionData.addon_orders.forEach((item: any) =>
        addAddon(item, 'addon_orders'),
      );
    }
    if (Array.isArray(this.sessionData?.addon_transactions)) {
      this.sessionData.addon_transactions.forEach((item: any) =>
        addAddon(item, 'addon_transactions'),
      );
    }

    // 7. Check items inside order_details with addon type/flag
    if (Array.isArray(this.sessionData?.order?.order_details)) {
      this.sessionData.order.order_details.forEach((item: any) => {
        const type = String(
          item.order_type || item.product_type || item.type_product || '',
        ).toLowerCase();
        if (type.includes('addon') || item.is_addon || item.addon_id != null) {
          addAddon(item, 'order_detail_addon');
        }
      });
    }

    // 8. Local browser addon transactions
    this.localAddonTransactions.forEach((item) => addAddon(item, 'local_tx'));

    return Array.from(addonsMap.values());
  }

  get mainPurchasedItems(): any[] {
    const all = this.purchasedItems;
    const addons = this.purchasedAddonItems;
    const addonIds = new Set(addons.map((a) => String(a.id)));

    return all.filter((item: any) => {
      const id = String(item.id || item.product_name);
      if (addonIds.has(id)) return false;
      const type = String(
        item.order_type || item.product_type || item.type_product || '',
      ).toLowerCase();
      if (type.includes('addon') || item.is_addon) return false;
      return true;
    });
  }

  get addonSubtotal(): number {
    return this.purchasedAddonItems.reduce(
      (acc, item) =>
        acc + Number(item.sub_total ?? item.product_price ?? item.total ?? 0),
      0,
    );
  }

  get addonDiscount(): number {
    return this.purchasedAddonItems.reduce(
      (acc, item) => acc + Number(item.discount ?? 0),
      0,
    );
  }

  get addonTotal(): number {
    return this.purchasedAddonItems.reduce(
      (acc, item) =>
        acc +
        Number(
          item.total ??
            Number(item.sub_total || 0) - Number(item.discount || 0),
        ),
      0,
    );
  }

  get overallGrandTotal(): number {
    const separateAddons = this.purchasedAddonItems.filter(
      (a) => a.source !== 'order_detail_addon',
    );
    const separateAddonTotal = separateAddons.reduce(
      (acc, item) => acc + Number(item.total ?? 0),
      0,
    );
    return this.orderTotal + separateAddonTotal;
  }

  get orderSubtotal(): number {
    if (this.order?.sub_total != null) return Number(this.order.sub_total);
    if (this.purchasedItems.length > 0) {
      return this.purchasedItems.reduce(
        (acc, item) => acc + Number(item.sub_total ?? item.product_price ?? 0),
        0,
      );
    }
    return Number(this.order?.total ?? 0);
  }

  get orderDiscount(): number {
    if (this.order?.discount != null) return Number(this.order.discount);
    if (this.purchasedItems.length > 0) {
      return this.purchasedItems.reduce(
        (acc, item) =>
          acc +
          Number(
            item.discount ??
              item.discount_source_details?.voucher_discount ??
              0,
          ),
        0,
      );
    }
    return 0;
  }

  get orderTax(): number {
    if (this.order?.tax != null) return Number(this.order.tax);
    if (this.purchasedItems.length > 0) {
      return this.purchasedItems.reduce(
        (acc, item) => acc + Number(item.tax ?? 0),
        0,
      );
    }
    return 0;
  }

  get orderTotal(): number {
    if (this.order?.total != null) return Number(this.order.total);
    const sub = this.orderSubtotal;
    const disc = this.orderDiscount;
    const tax = this.orderTax;
    return Math.max(0, sub - disc + tax);
  }

  get orderVoucherName(): string {
    if (this.order?.voucher_name) return this.order.voucher_name;
    for (const item of this.purchasedItems) {
      if (item.voucher_name) return item.voucher_name;
      if (item.discount_source_details?.voucher_name)
        return item.discount_source_details.voucher_name;
    }
    return '';
  }

  get lockedSoftFiles(): any[] {
    return this.softFiles.filter((file) => !this.isFileAuthorized(file));
  }

  Math = Math;

  get maxRedeemableCount(): number {
    return Math.min(this.softFileRemaining, this.lockedSoftFiles.length);
  }

  get softFileRemaining(): number {
    const pool = this.sessionData?.shared_pool_status;
    if (pool?.soft_file_remaining != null)
      return Number(pool.soft_file_remaining);
    if (this.sessionData?.soft_file_remaining != null)
      return Number(this.sessionData.soft_file_remaining);
    if (this.sessionData?.order?.soft_file_remaining != null)
      return Number(this.sessionData.order.soft_file_remaining);
    return 0;
  }

  private getOrderPin(): string {
    const segment = this.route.snapshot.paramMap.get('segment');
    return (
      this.sessionData?.order?.order_pin ||
      this.sessionData?.sessions?.[0]?.pin ||
      this.sessionData?.pin ||
      segment ||
      ''
    );
  }

  get qrPayload(): string {
    return String(
      this.sessionData?.qr_url ||
        this.sessionData?.order?.qr_url ||
        window.location.href,
    );
  }

  get qrUrl(): string {
    return `https://api.qrserver.com/v1/create-qr-code/?size=600x600&margin=12&data=${encodeURIComponent(this.qrPayload)}`;
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private apiService: ApiService,
    private feedbackService: FeedbackService,
    private dashboardService: DashboardService,
  ) {}

  ngOnInit(): void {
    const segment = this.route.snapshot.paramMap.get('segment');
    if (segment) {
      this.loadLocalAddonTransactions(segment);
      this.fetchData(segment);
    } else {
      this.isLoading = false;
    }

    // Fetch addon packages silently on init to update price dynamically
    this.fetchAddonPackages(false);
  }

  private currentSegment: string | null = null;

  loadLocalAddonTransactions(pin: string): void {
    try {
      if (pin) {
        const stored = localStorage.getItem(`addon_tx_${pin}`);
        if (stored) {
          const list = JSON.parse(stored);
          if (Array.isArray(list)) {
            this.localAddonTransactions = list;
          }
        }
      }
    } catch (_) {}
  }

  recordAddonPurchase(pkg: any, zeusData: any): void {
    const pin = this.getOrderPin();
    const pkgName = this.formatProductName(
      pkg?.name || 'Paket Buka Semua Foto (Soft File)',
    );
    const amount = Number(
      zeusData?.amount ?? pkg?.price ?? this.allFilesPrice ?? 0,
    );
    const invoice =
      zeusData?.invoice ||
      zeusData?.order_number ||
      zeusData?.reference_id ||
      zeusData?.id ||
      `INV-ADDON-${Date.now()}`;
    const newTx = {
      id: zeusData?.id || zeusData?.reference_id || `addon_${Date.now()}`,
      invoice: invoice,
      order_id: zeusData?.id || null,
      product_name: pkgName,
      name: pkgName,
      product_sku: pkg?.sku || 'ADDON-SOFTFILE',
      product_price: amount,
      sub_total: amount,
      discount: Number(zeusData?.discount ?? 0),
      voucher_name: this.appliedVoucher?.code || null,
      total: Math.max(0, amount - Number(zeusData?.discount ?? 0)),
      status: 'Paid',
      created_at: new Date().toISOString(),
      is_addon: true,
      source: 'local_tx',
    };
    this.localAddonTransactions.push(newTx);
    try {
      if (pin) {
        const key = `addon_tx_${pin}`;
        const existing = JSON.parse(localStorage.getItem(key) || '[]');
        existing.push(newTx);
        localStorage.setItem(key, JSON.stringify(existing));
      }
    } catch (_) {}
  }

  fetchData(segment: string) {
    if (this.currentSegment !== segment) {
      this.currentSegment = segment;
      this.hasAutoRedeemed = false;
      this.reviewPopupHandled = false;
      this.isCheckingReview = false;
      if (this.isReviewModalOpen) {
        this.isReviewModalOpen = false;
        this.reviewTargetSession = null;
        document.body.style.overflow = '';
      }
      this.loadLocalAddonTransactions(segment);
    }
    // Only call orders endpoint since this component is only for /r/ route
    this.apiService.get<any>(`/public/orders/${segment}`).subscribe({
      next: (res) => {
        if (this.currentSegment !== segment) {
          return;
        }

        if (res.success && res.data) {
          this.sessionData = res.data;
          this.loadLocalAddonTransactions(this.getOrderPin());
          this.fetchFotoinAjaMatches();

          // Satu review pada session mana pun berlaku untuk seluruh PIN.
          this.tryOpenReviewAfterPhoto();
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error fetching data', err);
        this.isLoading = false;
      },
    });
  }

  getFotoinAjaHostHeader(): string {
    if (
      typeof window !== 'undefined' &&
      window.location?.hostname === 'client.photobooth.awh.co.id'
    ) {
      return 'client.photobooth.awh.co.id';
    }
    return 'dev.client.photobooth.awh.co.id';
  }

  private formatDateToYMD(dateInput: any): string {
    if (!dateInput) return '';
    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  get isUltimaProduct(): boolean {
    if (!this.sessionData) return false;

    const isUltimaName = (val: any) => {
      if (!val) return false;
      const s = String(val).trim();
      return s === 'Ultima' || s.includes('Ultima');
    };

    if (
      isUltimaName(this.sessionData.product_name) ||
      isUltimaName(this.sessionData.details?.product_name) ||
      isUltimaName(this.sessionData.order?.product_name) ||
      isUltimaName(this.sessionData.order_detail?.product_name) ||
      isUltimaName(this.sessionData.order_detail?.details?.product_name)
    ) {
      return true;
    }

    if (Array.isArray(this.sessionData.sessions)) {
      for (const s of this.sessionData.sessions) {
        if (
          isUltimaName(s?.product_name) ||
          isUltimaName(s?.details?.product_name) ||
          isUltimaName(s?.order_detail?.product_name) ||
          isUltimaName(s?.order_detail?.details?.product_name)
        ) {
          return true;
        }
      }
    }

    const detailsArray =
      this.sessionData.order?.order_details ||
      this.sessionData.order_details ||
      [];
    if (Array.isArray(detailsArray)) {
      for (const item of detailsArray) {
        if (
          isUltimaName(item?.product_name) ||
          isUltimaName(item?.details?.product_name) ||
          isUltimaName(item?.product?.name)
        ) {
          return true;
        }
      }
    }

    if (this.mainPurchasedItems && this.mainPurchasedItems.length > 0) {
      for (const item of this.mainPurchasedItems) {
        if (
          isUltimaName(item?.product_name) ||
          isUltimaName(item?.details?.product_name) ||
          isUltimaName(item?.name)
        ) {
          return true;
        }
      }
    }

    return false;
  }

  fetchFotoinAjaMatches() {
    // Fotoin Aja matches only apply to Ultima product orders.
    // If it is NOT an Ultima product, do not fetch or show Fotoin Aja.
    if (!this.isUltimaProduct) {
      this.fotoinAjaMatches = [];
      this.isFotoinAjaLoading = false;
      return;
    }

    const referenceId = this.sessionData?.order?.order_pin_hash;
    if (!referenceId) return;

    this.isFotoinAjaLoading = true;
    const rawDate =
      this.sessionData?.order?.created_at ||
      this.sessionData?.created_at ||
      this.order?.created_at ||
      this.sessionData?.sessions?.[0]?.created_at ||
      new Date();

    const d =
      rawDate && !isNaN(new Date(rawDate).getTime())
        ? new Date(rawDate)
        : new Date();
    const startDateObj = new Date(d);
    startDateObj.setDate(startDateObj.getDate() - 3);
    const endDateObj = new Date(d);
    endDateObj.setDate(endDateObj.getDate() + 3);

    const startDateStr = this.formatDateToYMD(startDateObj);
    const endDateStr = this.formatDateToYMD(endDateObj);

    const url = `https://api.fotoinaja.id/api/v1/external-matches?page=1&per_page=50&reference_id=${referenceId}&start_date=${startDateStr}&end_date=${endDateStr}&similarity=0.50`;

    const host = this.getFotoinAjaHostHeader();
    const headers = new HttpHeaders({
      'X-HOST': host,
    });

    this.http.get<any>(url, { headers }).subscribe({
      next: (res) => {
        if (res && res.matches) {
          this.fotoinAjaMatches = res.matches.map((m: any, index: number) => ({
            ...m,
            url: m.matched_file || m.matched_thumbnail,
            name: `fotoin_aja_${index + 1}.jpg`,
            id: m.matched_photo_id || `fotoin_aja_${index}`,
            is_redeemed: true,
            is_fotoin_aja: true,
          }));
        }
        this.isFotoinAjaLoading = false;
      },
      error: (err) => {
        console.error('Failed to fetch Fotoin Aja matches', err);
        this.isFotoinAjaLoading = false;
      },
    });
  }

  // ==========================================================
  // SATU REVIEW UNTUK SELURUH PHOTO SESSION DALAM SATU PIN
  // ==========================================================

  /**
   * PENTING:
   * Review pada satu photo session menandai seluruh PIN sudah direview.
   * Periksa semua session yang mempunyai hasil foto sebelum membuka modal.
   */
  private reviewPopupHandled = false;
  private isCheckingReview = false;
  private getPhotoSessionId(session: any): string {
    const id =
      session?.id ?? session?.photo_session_id ?? session?.session_id ?? '';

    return String(id);
  }

  private getSessionDeviceId(session: any): string {
    const id =
      session?.device?.id ??
      session?.device_id ??
      session?.room?.id ??
      session?.room_id ??
      '';

    return String(id);
  }

  /**
   * Menentukan SATU session yang mewakili room yang sedang dipilih.
   *
   * Prioritas:
   * 1. selected/current/active photo_session_id dari response API.
   * 2. selected/current device/room id dari response API.
   * 3. flag is_selected / is_current pada session.
   * 4. fallback ke session PALING BARU saja.
   *
   * Tidak pernah mencari room lama hanya karena room lama sudah punya foto.
   */
  private getSelectedRoomSession(): any | null {
    const sessions = this.sessionData?.sessions;

    if (!Array.isArray(sessions) || sessions.length === 0) {
      return null;
    }

    // --------------------------------------------------------
    // 1. Kalau backend menyediakan photo_session_id aktif/terpilih
    // --------------------------------------------------------
    const explicitPhotoSessionId =
      this.sessionData?.selected_photo_session_id ??
      this.sessionData?.current_photo_session_id ??
      this.sessionData?.active_photo_session_id ??
      this.sessionData?.photo_session_id ??
      this.sessionData?.order?.selected_photo_session_id ??
      this.sessionData?.order?.current_photo_session_id ??
      this.sessionData?.order?.active_photo_session_id ??
      null;

    if (explicitPhotoSessionId != null) {
      const matchedSession = sessions.find(
        (session: any) =>
          this.getPhotoSessionId(session) === String(explicitPhotoSessionId),
      );

      if (matchedSession) {
        return matchedSession;
      }
    }

    // --------------------------------------------------------
    // 2. Kalau backend menyediakan room/device yang dipilih
    // --------------------------------------------------------
    const explicitDeviceId =
      this.sessionData?.selected_device_id ??
      this.sessionData?.current_device_id ??
      this.sessionData?.active_device_id ??
      this.sessionData?.selected_room_id ??
      this.sessionData?.current_room_id ??
      this.sessionData?.order?.selected_device_id ??
      this.sessionData?.order?.current_device_id ??
      this.sessionData?.order?.selected_room_id ??
      null;

    if (explicitDeviceId != null) {
      const matchedByRoom = [...sessions]
        .reverse()
        .find(
          (session: any) =>
            this.getSessionDeviceId(session) === String(explicitDeviceId),
        );

      if (matchedByRoom) {
        return matchedByRoom;
      }
    }

    // --------------------------------------------------------
    // 3. Flag session yang memang ditandai terpilih/current
    // --------------------------------------------------------
    const explicitlySelected = [...sessions]
      .reverse()
      .find(
        (session: any) =>
          session?.is_selected === true ||
          session?.is_current === true ||
          session?.selected === true,
      );

    if (explicitlySelected) {
      return explicitlySelected;
    }

    // --------------------------------------------------------
    // 4. Fallback: ambil SESSION TERBARU SAJA.
    //    Tidak mencari semua session.
    // --------------------------------------------------------
    const sessionsWithCreatedTime = sessions
      .map((session: any, index: number) => ({
        session,
        index,
        time: this.getSessionCreatedTime(session),
      }))
      .filter((item: any) => item.time > 0);

    if (sessionsWithCreatedTime.length > 0) {
      sessionsWithCreatedTime.sort(
        (a: any, b: any) => b.time - a.time || b.index - a.index,
      );

      return sessionsWithCreatedTime[0].session;
    }

    // Kalau response tidak mempunyai timestamp,
    // gunakan item terakhir dari array sebagai session terbaru.
    return sessions[sessions.length - 1] ?? null;
  }

  private getSessionCreatedTime(session: any): number {
    const rawDate =
      session?.created_at ??
      session?.started_at ??
      session?.start_at ??
      session?.createdAt ??
      null;

    if (!rawDate) {
      return 0;
    }

    const time = new Date(rawDate).getTime();

    return Number.isFinite(time) ? time : 0;
  }

  private sessionHasPhoto(session: any): boolean {
    const hasSoftFiles =
      Array.isArray(session?.soft_files) && session.soft_files.length > 0;

    const hasFramedPhotos =
      Array.isArray(session?.framed_photos) && session.framed_photos.length > 0;

    const hasAiPhotos =
      Array.isArray(session?.ai_photos) && session.ai_photos.length > 0;

    return (
      hasSoftFiles ||
      hasFramedPhotos ||
      hasAiPhotos ||
      Boolean(session?.framed_photo || session?.soft_file || session?.ai_photo)
    );
  }

  private getReviewableSessions(): any[] {
    const sessions = this.sessionData?.sessions;

    if (!Array.isArray(sessions)) {
      return [];
    }

    return sessions.filter(
      (session: any) =>
        Boolean(this.getPhotoSessionId(session)) && this.sessionHasPhoto(session),
    );
  }

  private getSessionReview(session: any): any | null {
    if (session?.review) {
      return session.review;
    }

    if (Array.isArray(session?.reviews) && session.reviews.length > 0) {
      return session.reviews[0];
    }

    if (
      session?.review_id ||
      session?.has_review === true ||
      session?.is_reviewed === true
    ) {
      return { id: session.review_id || true };
    }

    const photoSessionId = this.getPhotoSessionId(session);
    const backendReviews = [
      ...(Array.isArray(this.sessionData?.reviews)
        ? this.sessionData.reviews
        : []),
      ...(Array.isArray(this.sessionData?.order?.reviews)
        ? this.sessionData.order.reviews
        : []),
    ];

    return (
      backendReviews.find(
        (review: any) =>
          String(
            review?.photo_session_id ??
              review?.photoSessionId ??
              review?.photo_session?.id ??
              '',
          ) ===
          photoSessionId,
      ) || null
    );
  }

  private checkDashboardReview(
    photoSessionId: string,
    next: (review: any | null) => void,
    error: (err: any) => void,
  ): void {
    this.dashboardService
      .reviewList({
        photo_session_id: photoSessionId,
        page: 1,
        per_page: 100,
      })
      .subscribe({
        next: (rawResponse: any) => {
          const response = rawResponse?.body ?? rawResponse;
          const reviews = Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response?.data?.data)
              ? response.data.data
              : Array.isArray(response)
                ? response
                : [];

          const review = reviews.find(
            (item: any) =>
              String(
                item?.photo_session_id ??
                  item?.photoSessionId ??
                  item?.photo_session?.id ??
                  '',
              ) === photoSessionId,
          );

          next(review ?? null);
        },
        error,
      });
  }

  /** Satu review pada session mana pun berarti PIN ini sudah direview. */
  private checkAnyReviewForSegment(
    sessions: any[],
    index: number,
    next: (review: any | null) => void,
    error: (err: any) => void,
  ): void {
    if (index >= sessions.length) {
      next(null);
      return;
    }

    const session = sessions[index];
    const localReview = this.getSessionReview(session);

    if (localReview) {
      next(localReview);
      return;
    }

    const photoSessionId = this.getPhotoSessionId(session);

    if (!photoSessionId) {
      this.checkAnyReviewForSegment(sessions, index + 1, next, error);
      return;
    }

    this.checkDashboardReview(
      photoSessionId,
      (existingReview) => {
        if (existingReview) {
          next(existingReview);
          return;
        }

        this.checkAnyReviewForSegment(sessions, index + 1, next, error);
      },
      error,
    );
  }

  private getSingleReviewTarget(): any | null {
    const selectedSession = this.getSelectedRoomSession();

    if (
      selectedSession &&
      this.getPhotoSessionId(selectedSession) &&
      this.sessionHasPhoto(selectedSession)
    ) {
      return selectedSession;
    }

    const sessions = this.getReviewableSessions();

    if (sessions.length === 0) {
      return null;
    }

    return [...sessions].sort(
      (a: any, b: any) =>
        this.getSessionCreatedTime(b) - this.getSessionCreatedTime(a),
    )[0] ?? null;
  }

  private tryOpenReviewAfterPhoto(): void {
    if (
      this.reviewPopupHandled ||
      this.isReviewModalOpen ||
      this.isCheckingReview
    ) {
      return;
    }

    const sessions = this.getReviewableSessions();

    if (sessions.length === 0) {
      return;
    }

    this.isCheckingReview = true;
    const checkedSegment = this.currentSegment;

    this.checkAnyReviewForSegment(
      sessions,
      0,
      (existingReview) => {
        if (this.currentSegment !== checkedSegment) {
          return;
        }

        this.isCheckingReview = false;

        if (existingReview) {
          this.reviewPopupHandled = true;
          this.isReviewModalOpen = false;
          this.reviewTargetSession = null;
          return;
        }

        const targetSession = this.getSingleReviewTarget();

        if (!targetSession) {
          return;
        }

        this.reviewTargetSession = targetSession;
        this.reviewRating = 0;
        this.reviewNote = '';
        this.isReviewModalOpen = true;
        document.body.style.overflow = 'hidden';
      },
      (err) => {
        if (this.currentSegment !== checkedSegment) {
          return;
        }

        this.isCheckingReview = false;
        console.error('[Review] Gagal mengecek review segment:', err);
        // Jangan membuka modal saat status database tidak dapat dipastikan.
      },
    );
  }

  closeReviewModal(): void {
    if (this.isSubmittingReview) {
      return;
    }

    // Cegah popup dibuka berulang selama halaman yang sama masih aktif.
    // Setelah refresh, status akan diperiksa ulang dari response backend.
    this.reviewPopupHandled = true;

    this.isReviewModalOpen = false;
    this.reviewTargetSession = null;
    this.reviewRating = 0;
    this.reviewNote = '';

    document.body.style.overflow = '';
  }

  setReviewRating(value: number): void {
    if (value < 1 || value > 5) {
      return;
    }

    this.reviewRating = value;
  }

  submitReview(): void {
    if (this.isSubmittingReview) {
      return;
    }

    const photoSessionId = this.getPhotoSessionId(this.reviewTargetSession);

    if (!photoSessionId) {
      Swal.fire({
        icon: 'error',
        title: 'Gagal',
        text: 'Photo session ID tidak ditemukan.',
      });

      return;
    }

    if (this.reviewRating < 1 || this.reviewRating > 5) {
      Swal.fire({
        icon: 'warning',
        title: 'Pilih Rating',
        text: 'Silakan pilih rating terlebih dahulu.',
      });

      return;
    }

    const payload = {
      photo_session_id: photoSessionId,
      rating: this.reviewRating,
      note: this.reviewNote.trim(),
    };

    const sessions = this.getReviewableSessions();

    if (sessions.length === 0) {
      this.showReviewVerificationError();
      return;
    }

    this.isSubmittingReview = true;

    // Periksa ulang seluruh PIN sesaat sebelum POST.
    this.checkAnyReviewForSegment(
      sessions,
      0,
      (existingReview) => {
        if (existingReview) {
          this.isSubmittingReview = false;
          this.handleAlreadyReviewed();
          return;
        }

        this.sendVerifiedReview(payload, photoSessionId);
      },
      (err) => {
        this.isSubmittingReview = false;
        console.error('[Review] Gagal memverifikasi review segment:', err);
        this.showReviewVerificationError();
      },
    );
  }

  private showReviewVerificationError(
    message = 'Status review tidak dapat diverifikasi. Silakan coba lagi.',
  ): void {
    Swal.fire({
      icon: 'error',
      title: 'Gagal Memeriksa Review',
      text: message,
    });
  }

  private sendVerifiedReview(
    payload: { photo_session_id: string; rating: number; note: string },
    photoSessionId: string,
  ): void {
    // POST /api/v1/public/reviews
    this.feedbackService.sendPhotoSessionReview(payload).subscribe({
      next: (res) => {
        this.isSubmittingReview = false;

        if (res?.success === false) {
          if (this.isDuplicateReviewResponse(res)) {
            this.handleAlreadyReviewed();
            return;
          }

          Swal.fire({
            icon: 'error',
            title: 'Gagal',
            text: res?.message || 'Review gagal dikirim.',
          });

          return;
        }

        if (this.reviewTargetSession) {
          this.reviewTargetSession.review = res?.data || {
            photo_session_id: photoSessionId,
            rating: this.reviewRating,
            note: this.reviewNote.trim(),
          };
        }

        // Satu submit sukses berlaku untuk seluruh PIN.
        this.reviewPopupHandled = true;

        this.isReviewModalOpen = false;
        this.reviewTargetSession = null;
        this.reviewRating = 0;
        this.reviewNote = '';

        document.body.style.overflow = '';

        Swal.fire({
          icon: 'success',
          title: 'Terima Kasih!',
          text: 'Ulasan kamu berhasil dikirim.',
          confirmButtonColor: '#1689ee',
        });
      },

      error: (err) => {
        this.isSubmittingReview = false;

        console.error('[Photo Session Review] Failed:', err);

        if (this.isDuplicateReviewResponse(err)) {
          this.handleAlreadyReviewed();
          return;
        }

        Swal.fire({
          icon: 'error',
          title: 'Gagal',
          text:
            err?.error?.message || 'Ulasan gagal dikirim. Silakan coba lagi.',
        });
      },
    });
  }

  private isDuplicateReviewResponse(value: any): boolean {
    const status = Number(value?.status ?? value?.error?.status ?? 0);
    const message = String(
      value?.message ?? value?.error?.message ?? value?.error ?? '',
    ).toLowerCase();

    return (
      status === 409 ||
      message.includes('already reviewed') ||
      message.includes('already exists') ||
      message.includes('duplicate') ||
      message.includes('sudah direview') ||
      message.includes('sudah pernah review') ||
      message.includes('review sudah')
    );
  }

  private handleAlreadyReviewed(): void {
    this.isSubmittingReview = false;
    this.reviewPopupHandled = true;
    this.isReviewModalOpen = false;
    this.reviewTargetSession = null;
    this.reviewRating = 0;
    this.reviewNote = '';
    document.body.style.overflow = '';

    Swal.fire({
      icon: 'info',
      title: 'Review Sudah Tercatat',
      text: 'PIN ini sudah pernah direview dan tidak dapat direview kembali.',
      confirmButtonColor: '#1689ee',
    });
  }

  isFileAuthorized(file: any): boolean {
    if (!file) return false;
    return (
      this.isAllFilesUnlocked ||
      file.is_redeemed === true ||
      file.is_purchased === true ||
      file.is_downloadable === true ||
      file.is_unlocked === true
    );
  }

  formatPrice(value: number): string {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(value);
  }

  formatProductName(name: string | null | undefined): string {
    if (!name || typeof name !== 'string') return name ? String(name) : '';

    let formatted = name.trim();

    // Check if it's an auto-redeem soft file item with limit/999 or debug text
    if (/auto-?redeem.*soft.*file/i.test(formatted)) {
      return 'Auto-redeem All Soft Files (Unlimited)';
    }

    if (/soft_file_limit/i.test(formatted)) {
      formatted = formatted.replace(
        /\(soft_file_limit\s*[><=]*\s*\d*\)/gi,
        '(Unlimited)',
      );
      formatted = formatted.replace(
        /soft_file_limit\s*[><=]*\s*\d*/gi,
        'Unlimited',
      );
      formatted = formatted.replace(/soft_file_limit/gi, 'Soft File Unlimited');
    }

    if (
      /soft[_\s]*limit\s*[><=]*\s*999/i.test(formatted) ||
      />\s*999/i.test(formatted)
    ) {
      formatted = formatted.replace(
        /\(?soft[_\s]*limit\s*[><=]*\s*999\)?/gi,
        '(Unlimited)',
      );
      formatted = formatted.replace(/>\s*999/g, '(Unlimited)');
    }

    // Clean up duplicate "(Unlimited) (Unlimited)" or "Unlimited (Unlimited)"
    formatted = formatted.replace(/Unlimited\s*\(Unlimited\)/gi, 'Unlimited');
    formatted = formatted.replace(
      /\(Unlimited\)\s*\(Unlimited\)/gi,
      '(Unlimited)',
    );

    return formatted.replace(/\s+/g, ' ').trim();
  }

  qrisStatus: 'waiting' | 'success' = 'waiting';
  paymentQrUrl: string = '';
  paymentAmount: number = 0;
  paymentCountdown: string = '';
  paymentInterval: any = null;
  isCancelPaymentModalOpen: boolean = false;

  // Shop Modal & Voucher State
  isShopModalOpen = false;
  addonPackages: any[] = [];
  mqttClient: any = null;
  isAddonLoading = false;

  // Voucher State
  voucherCodeInput = '';
  isApplyingVoucher = false;
  voucherError = '';
  appliedVoucher: any = null;
  appliedVoucherId: number | null = null;

  openCheckout(mode: 'single' | 'all', file?: any): void {
    if (file) {
      this.selectedFile = file;
    }
    this.fetchAddonPackages(true);
  }

  fetchAddonPackages(openModal = true): void {
    if (openModal) {
      this.isShopModalOpen = true;
    }
    this.isAddonLoading = true;
    const url = `${environment.zeusApiUrl}/api/v1/tenant/products?outlet_id=1&paginating=false&is_active=1&type_product=addon_photobooth_wls`;
    this.http.get<any>(url).subscribe({
      next: (res) => {
        if (res.success && res.data && res.data.products) {
          const softFilePackages = res.data.products.filter((p: any) => {
            return p.properties?.limit_extensions?.soft_file_limit > 0;
          });
          const nonSatuanPackages = softFilePackages.filter((p: any) => {
            const name = String(p.name || '').toLowerCase();
            return !name.includes('satuan');
          });
          this.addonPackages =
            nonSatuanPackages.length > 0 ? nonSatuanPackages : softFilePackages;
        }
        this.isAddonLoading = false;
      },
      error: (err) => {
        console.error('Failed to load addon packages', err);
        this.isAddonLoading = false;
      },
    });
  }

  closeShopModal(): void {
    this.isShopModalOpen = false;
  }

  applyVoucher(codeToApply?: string): void {
    const code = (codeToApply || this.voucherCodeInput).trim().toUpperCase();
    if (!code) {
      this.voucherError = 'Silakan masukkan kode voucher';
      return;
    }

    this.isApplyingVoucher = true;
    this.voucherError = '';
    this.appliedVoucher = null;
    this.appliedVoucherId = null;

    const customerPhone = this.sessionData?.order?.customer_phone || '';
    const payload = {
      voucher_code: code,
      customer_phone: customerPhone || undefined,
      outlet_id: 1,
    };

    const url = `${environment.zeusApiUrl}/api/v1/tenant/voucher/redeem`;
    this.http.post<any>(url, payload).subscribe({
      next: (res) => {
        this.isApplyingVoucher = false;
        const responseData = res?.data?.data || res?.data || res;
        if (res?.success || res?.data) {
          this.appliedVoucher = responseData;
          this.appliedVoucherId =
            responseData?.voucher_id ||
            responseData?.id ||
            responseData?.context?.[0]?.voucher_id ||
            responseData?.context?.[0]?.id ||
            null;

          this.isShopModalOpen = true;

          Swal.fire({
            icon: 'success',
            title: 'Voucher Berhasil Diterapkan!',
            text: `Voucher "${code}" berhasil digunakan. Silakan pilih paket produk yang ingin kamu klaim!`,
            confirmButtonColor: '#3b82f6',
          });
        } else {
          this.voucherError =
            res?.message || 'Kode voucher tidak valid atau sudah kadaluarsa.';
        }
      },
      error: (err) => {
        this.isApplyingVoucher = false;
        console.error('Failed to redeem voucher', err);
        const errMsg =
          err?.error?.message ||
          err?.error?.data?.message ||
          'Kode voucher tidak valid atau tidak dapat digunakan.';
        this.voucherError = errMsg;
        Swal.fire({
          icon: 'error',
          title: 'Voucher Gagal',
          text: errMsg,
        });
      },
    });
  }

  claimFilesWithVoucher(): void {
    if (!this.appliedVoucherId) return;

    const contextList = this.appliedVoucher?.context || [];
    const contextProductIds = contextList.map((c: any) => c.product_id);

    const executeClaim = () => {
      if (this.addonPackages.length > 0) {
        const matchedPackage =
          this.addonPackages.find((p: any) =>
            contextProductIds.includes(p.id),
          ) || this.addonPackages[0];
        this.buyAddon(matchedPackage.id, this.appliedVoucherId);
      }
    };

    if (this.addonPackages.length > 0) {
      executeClaim();
    } else {
      this.isAddonLoading = true;
      const url = `${environment.zeusApiUrl}/api/v1/tenant/products?outlet_id=1&paginating=false&is_active=1&type_product=addon_photobooth_wls`;
      this.http.get<any>(url).subscribe({
        next: (res) => {
          this.isAddonLoading = false;
          if (res.success && res.data && res.data.products) {
            const softFilePackages = res.data.products.filter((p: any) => {
              return p.properties?.limit_extensions?.soft_file_limit > 0;
            });
            const nonSatuanPackages = softFilePackages.filter((p: any) => {
              const name = String(p.name || '').toLowerCase();
              return !name.includes('satuan');
            });
            this.addonPackages =
              nonSatuanPackages.length > 0
                ? nonSatuanPackages
                : softFilePackages;
            executeClaim();
          }
        },
        error: (err) => {
          this.isAddonLoading = false;
          console.error('Failed to fetch packages for voucher claim', err);
        },
      });
    }
  }

  removeVoucher(): void {
    this.appliedVoucher = null;
    this.appliedVoucherId = null;
    this.voucherCodeInput = '';
    this.voucherError = '';
  }

  getVoucherDiscountText(): string {
    if (!this.appliedVoucher) return '';
    const ctx = this.appliedVoucher?.context?.[0];
    const isPercent =
      ctx?.is_percentage ??
      (this.appliedVoucher?.discount_type === 'percentage' ||
        this.appliedVoucher?.discount_type === 'percent');
    const val = Number(
      ctx?.discount_value ?? this.appliedVoucher?.discount_value ?? 0,
    );

    if (isPercent) {
      return `Diskon ${val}%`;
    } else if (val > 0) {
      return `Potongan Rp${val.toLocaleString('id-ID')}`;
    }
    return 'Diskon Voucher';
  }

  getDiscountedPrice(
    price: number,
    productId: number,
  ): { finalPrice: number; isFree: boolean; discountText: string } {
    if (!this.appliedVoucher) {
      return { finalPrice: price, isFree: false, discountText: '' };
    }

    const contextList = this.appliedVoucher?.context || [];
    const ctx =
      contextList.find(
        (c: any) => Number(c.product_id) === Number(productId),
      ) ||
      contextList[0] ||
      this.appliedVoucher;

    if (!ctx) return { finalPrice: price, isFree: false, discountText: '' };

    const isPercent =
      ctx?.is_percentage ??
      (this.appliedVoucher?.discount_type === 'percentage' ||
        this.appliedVoucher?.discount_type === 'percent');
    const val = Number(
      ctx?.discount_value ?? this.appliedVoucher?.discount_value ?? 0,
    );

    let finalPrice = price;
    let discountText = '';

    if (isPercent) {
      const disc = (price * val) / 100;
      finalPrice = Math.max(0, price - disc);
      discountText = `Diskon ${val}%`;
    } else if (val > 0) {
      finalPrice = Math.max(0, price - val);
      discountText = `Potongan Rp${val.toLocaleString('id-ID')}`;
    }

    return {
      finalPrice,
      isFree: finalPrice <= 0,
      discountText,
    };
  }

  buyAddon(
    packageId: string | number,
    voucherIdOverride?: number | null,
  ): void {
    const customerName = this.sessionData?.order?.name || 'Guest';
    const customerPhone = this.sessionData?.order?.customer_phone || '';
    const referenceCode =
      this.sessionData?.order?.external_code ||
      this.sessionData?.order?.order_pin_hash ||
      '';
    const voucherIdToUse =
      voucherIdOverride !== undefined
        ? voucherIdOverride
        : this.appliedVoucherId;

    const matchedPackage = this.addonPackages.find(
      (p: any) => p.id === Number(packageId),
    ) ||
      this.addonPackages.find((p: any) => p.id === packageId) || {
        id: Number(packageId),
        name: 'Paket Buka Semua Foto (Soft File)',
        price: this.allFilesPrice,
      };
    this.pendingPurchasedPackage = matchedPackage;

    const payload: any = {
      customer_name: customerName,
      customer_phone: customerPhone,
      outlet_id: 1,
      no_table: '1',
      payment_gateway_id: null,
      orders: {
        products: [
          {
            id: Number(packageId),
            quantity: 1,
            quantities: [
              {
                note: '',
                takeaway: false,
                addon: [],
                addon_integration: [],
              },
            ],
          },
        ],
        voucher_id: voucherIdToUse || null,
      },
    };

    if (referenceCode) {
      payload.data_info = {
        reference_code: referenceCode,
      };
    }

    this.isCheckoutLoading = true;
    this.checkoutError = '';
    this.isShopModalOpen = false;

    const url = `${environment.zeusApiUrl}/api/v1/tenant/orders`;
    this.http.post(url, payload).subscribe({
      next: (res: any) => {
        this.isCheckoutLoading = false;
        console.log('Order addon created successfully', res);
        const data = res?.data || {};
        const qrString =
          data.payment_string ||
          data.payment_response?.qr_string ||
          data.payment_response?.qr_code ||
          data.payment_response?.payment_string ||
          data.payment_response?.payment_url ||
          data.payment_response?.data?.qr_string ||
          data.payment_response?.data?.qr_code ||
          data.payment_response?.data?.payment_string ||
          data.payment_data?.qr_string ||
          data.payment_data?.qr_code ||
          data.payment_data?.payment_string ||
          data.qr_string ||
          data.qr_code ||
          data.qr_url ||
          data.qris_string ||
          data.qris ||
          data.payment_url ||
          res?.payment_string ||
          res?.qr_string ||
          res?.qr_code ||
          res?.qr_url ||
          '';

        const rawAmount =
          data.amount ??
          data.total ??
          data.grand_total ??
          data.payment_response?.amount ??
          matchedPackage?.price;

        const amount = Number(rawAmount ?? 0);

        const isFreeOrNominal = amount <= 0;

        const isPaymentSuccess =
          data.payment_status === 'PAID' ||
          data.payment_status === 'SUCCESS' ||
          data.payment_status === 'SUCCEEDED' ||
          data.payment_status === 'SETTLED' ||
          data.status === 'SUCCESS' ||
          data.status === 'SUCCEEDED' ||
          data.status === 'PAID';

        if (isFreeOrNominal || isPaymentSuccess) {
          this.qrisStatus = 'success';
          this.isAllFilesUnlocked = true;
          this.lastZeusOrderId = data.id || data.reference_id || null;
          this.recordAddonPurchase(matchedPackage, data);

          const segment = this.route.snapshot.paramMap.get('segment');
          // Wait 2.5s for WLS backend to sync quota from Zeus before redeeming
          setTimeout(() => {
            if (segment) {
              this.apiService
                .get<any>(`/public/orders/${encodeURIComponent(segment)}`)
                .subscribe({
                  next: (orderRes) => {
                    if (orderRes.success && orderRes.data) {
                      this.sessionData = orderRes.data;
                    }
                    this.autoRedeemAllFiles();
                  },
                  error: () => this.autoRedeemAllFiles(),
                });
            } else {
              this.autoRedeemAllFiles();
            }
          }, 2500);
          Swal.fire({
            icon: 'success',
            title: 'Klaim Berhasil!',
            text: 'Semua foto kamu telah berhasil dibuka!',
            confirmButtonColor: '#10b981',
          }).then(() => {
            window.location.reload();
          });
        } else {
          this.paymentAmount = amount || matchedPackage?.price || 0;
          this.isCheckoutModalOpen = true;
          this.qrisStatus = 'waiting';

          if (qrString) {
            if (
              qrString.startsWith('http://') ||
              qrString.startsWith('https://') ||
              qrString.startsWith('data:image')
            ) {
              this.paymentQrUrl = qrString;
            } else {
              this.paymentQrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrString)}`;
            }
            this.checkoutError = '';
            this.startPaymentCountdown(data.expires_at || data.expired_at);

            const referenceId = data.reference_id || data.id || '';
            if (referenceId) {
              this.listenToMqtt(referenceId);
            }
          } else {
            console.warn('QR string not found in server response', data);
            this.paymentQrUrl = '';
            this.checkoutError = 'Gagal memproses QRIS dari server. Silakan coba lagi.';
          }
        }
      },
      error: (err) => {
        this.isCheckoutLoading = false;
        console.error('Failed to create order addon', err);
        const errMsg =
          err?.error?.message ||
          err?.error?.data?.message ||
          'Gagal membuat pesanan paket tambahan. Silakan coba lagi.';
        Swal.fire({
          icon: 'error',
          title: 'Gagal',
          text: errMsg,
        });
      },
    });
  }

  listenToMqtt(referenceId: string): void {
    if (this.mqttClient) this.mqttClient.end();

    const url = 'wss://mqtt.adhivasindo.com:443/mqtt';
    const options = {
      clean: true,
      connectTimeout: 4000,
      clientId: crypto.randomUUID(),
      username: 'dhivapos',
      password: 'FurlaRasaMelon2024',
    };

    this.mqttClient = mqtt.connect(url, options);

    this.mqttClient.on('connect', () => {
      console.log('MQTT connected, subscribing to:', referenceId);
      this.mqttClient.subscribe(referenceId, { qos: 0 }, (err: any) => {
        if (err) console.error('MQTT Subscribe error:', err);
      });
    });

    this.mqttClient.on('message', (topic: string, payload: any) => {
      const payloadStr = payload.toString();
      console.log(`[MQTT] Message Received on ${topic}:`, payloadStr);

      try {
        const t = JSON.parse(payloadStr);
        if (
          t.status === 'SUCCEEDED' ||
          t.status == '1' ||
          t.status === 'SUCCESS' ||
          t.status === 'PAID' ||
          t.status === 'success' ||
          t.transaction_status === 'settlement' ||
          t.transaction_status === 'capture'
        ) {
          console.log('[MQTT] Payment Success Triggered!');
          if (this.paymentInterval) clearInterval(this.paymentInterval);
          if (this.mqttClient) this.mqttClient.end();

          this.qrisStatus = 'success';
          this.isAllFilesUnlocked = true;
          this.recordAddonPurchase(this.pendingPurchasedPackage, {
            id: referenceId,
            reference_id: referenceId,
            amount: this.paymentAmount,
            status: 'Paid',
          });
          this.autoRedeemAllFiles();
          Swal.fire({
            icon: 'success',
            title: 'Pembayaran Berhasil!',
            text: 'Semua foto kamu telah berhasil dibuka!',
            confirmButtonColor: '#10b981',
          }).then(() => {
            window.location.reload();
          });
        }
      } catch (e) {
        console.error('[MQTT] Error parsing MQTT message:', e);
      }
    });
  }

  closeCheckout(): void {
    if (!this.isCheckoutLoading) {
      this.isCheckoutModalOpen = false;
      this.isCancelPaymentModalOpen = false;
      if (this.qrisStatus === 'success') {
        window.location.reload();
      }
      this.qrisStatus = 'waiting';
      if (this.mqttClient) this.mqttClient.end();
      if (this.paymentInterval) clearInterval(this.paymentInterval);
    }
  }

  promptClosePayment(): void {
    this.isCancelPaymentModalOpen = true;
  }

  cancelClosePayment(): void {
    this.isCancelPaymentModalOpen = false;
  }

  confirmClosePayment(): void {
    this.closeCheckout();
  }

  // Removed unused confirmCheckout API (moved to buyAddon)

  startPaymentCountdown(expiresAtStr: string): void {
    if (this.paymentInterval) clearInterval(this.paymentInterval);
    if (!expiresAtStr) return;

    const updateCountdown = () => {
      const expiredTimestamp = new Date(expiresAtStr).getTime();
      const now = new Date().getTime();
      const diff = expiredTimestamp - now;

      if (diff <= 0 || isNaN(diff)) {
        this.paymentCountdown = 'Waktu habis';
        clearInterval(this.paymentInterval);
        if (this.mqttClient) this.mqttClient.end();
        this.checkoutError = 'Waktu pembayaran telah habis.';
      } else {
        const m = Math.floor((diff / 1000 / 60) % 60);
        const s = Math.floor((diff / 1000) % 60);
        this.paymentCountdown = `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
      }
    };

    updateCountdown();
    this.paymentInterval = setInterval(updateCountdown, 1000);
  }

  private refreshOrder(): void {
    const segment = this.route.snapshot.paramMap.get('segment');
    if (!segment) return;
    this.fetchData(segment);
  }

  private isAutoRedeeming = false;
  private hasAutoRedeemed = false;

  private getFileIntegerId(file: any): number | null {
    if (!file) return null;
    const rawId =
      file.media_id ??
      file.photo_id ??
      file.file_id ??
      file.backend_id ??
      file.id;

    if (rawId != null) {
      if (typeof rawId === 'number' && !isNaN(rawId) && rawId > 0) {
        return rawId;
      }
      if (typeof rawId === 'string') {
        const num = Number(rawId);
        if (!isNaN(num) && num > 0) {
          return num;
        }
        const match = rawId.match(/\d+/);
        if (match) {
          const extracted = Number(match[0]);
          if (!isNaN(extracted) && extracted > 0) {
            return extracted;
          }
        }
      }
    }
    return null;
  }

  private autoRedeemAllFiles(): void {
    if (this.isAutoRedeeming || this.hasAutoRedeemed) return;

    const segment = this.route.snapshot.paramMap.get('segment');
    if (!this.sessionData || !this.sessionData.sessions || !segment) {
      this.refreshOrder();
      return;
    }

    let mediaIds: number[] = [];
    this.sessionData.sessions.forEach((session: any) => {
      const lockedFiles =
        session.soft_files?.filter((f: any) => !this.isFileAuthorized(f)) || [];
      if (lockedFiles.length > 0) {
        lockedFiles.forEach((f: any) => {
          const id = this.getFileIntegerId(f);
          if (id != null && !mediaIds.includes(id)) {
            mediaIds.push(id);
          }
        });
      }
    });

    if (mediaIds.length > 0) {
      this.isAutoRedeeming = true;
      const url = `/public/orders/${segment}/soft-files/redeem`;
      this.apiService.post<any>(url, { media_ids: mediaIds }).subscribe({
        next: () => {
          this.isAutoRedeeming = false;
          this.hasAutoRedeemed = true;

          // Optimistically unlock files locally in memory
          this.sessionData.sessions?.forEach((session: any) => {
            session.soft_files?.forEach((f: any) => {
              f.is_redeemed = true;
              f.is_downloadable = true;
            });
          });

          console.log(`Auto-redeemed files for order ${segment}`);
          this.refreshOrder();
          window.location.reload();
        },
        error: (err) => {
          this.isAutoRedeeming = false;
          console.error(`Failed to auto-redeem for order ${segment}`, err);
          this.refreshOrder();
        },
      });
    } else {
      this.hasAutoRedeemed = true;
      this.refreshOrder();
    }
  }

  isRedeeming = false;

  getFileId(file: any): string {
    if (!file) return '';
    return String(file.id || file.photo_id || file.media_id || file.url || '');
  }

  openClaimLimitModal(): void {
    const locked = this.lockedSoftFiles;
    if (locked.length === 0) {
      Swal.fire('Informasi', 'Semua foto sudah terbuka!', 'info');
      return;
    }

    const limit = this.softFileRemaining;
    if (limit <= 0) {
      Swal.fire({
        title: 'Limit Kuota Habis',
        text: 'Limit klaim foto Anda sudah habis. Silakan beli paket tambahan untuk membuka foto lainnya.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Beli Paket Tambahan',
        cancelButtonText: 'Batal',
      }).then((res) => {
        if (res.isConfirmed) {
          this.openCheckout('all');
        }
      });
      return;
    }

    // Pre-select up to remaining limit for smooth UX
    const initialCount = Math.min(limit, locked.length);
    this.selectedRedeemFiles = locked.slice(0, initialCount);
    this.isClaimLimitModalOpen = true;
    document.body.style.overflow = 'hidden';
  }

  closeClaimLimitModal(): void {
    this.isClaimLimitModalOpen = false;
    this.selectedRedeemFiles = [];
    document.body.style.overflow = '';
  }

  toggleSelectRedeemFile(file: any): void {
    const targetId = this.getFileId(file);
    const index = this.selectedRedeemFiles.findIndex(
      (f) => this.getFileId(f) === targetId,
    );

    if (index > -1) {
      // Unselect
      this.selectedRedeemFiles.splice(index, 1);
    } else {
      // Select if within limit
      if (this.selectedRedeemFiles.length < this.softFileRemaining) {
        this.selectedRedeemFiles.push(file);
      } else {
        const Toast = Swal.mixin({
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 2500,
          timerProgressBar: true,
        });
        Toast.fire({
          icon: 'warning',
          title: `Maksimal memilih ${this.softFileRemaining} foto sesuai limit Anda`,
        });
      }
    }
  }

  isRedeemFileSelected(file: any): boolean {
    const targetId = this.getFileId(file);
    return this.selectedRedeemFiles.some((f) => this.getFileId(f) === targetId);
  }

  confirmRedeemSelected(): void {
    if (this.selectedRedeemFiles.length === 0) {
      Swal.fire('Peringatan', 'Pilih minimal 1 foto untuk diklaim.', 'warning');
      return;
    }
    const filesToClaim = [...this.selectedRedeemFiles];
    this.closeClaimLimitModal();
    this.redeemPhotos(filesToClaim);
  }

  async redeemPhotosLimit(): Promise<void> {
    this.openClaimLimitModal();
  }

  async redeemPhotos(targetFiles?: any[]): Promise<void> {
    if (this.isRedeeming) return;
    const pin = this.getOrderPin();
    if (!pin) {
      Swal.fire('Error', 'PIN order tidak ditemukan.', 'error');
      return;
    }

    let filesToRedeem =
      targetFiles && targetFiles.length > 0
        ? targetFiles
        : this.lockedSoftFiles;

    if (filesToRedeem.length === 0) {
      Swal.fire(
        'Peringatan',
        'Tidak ada foto terkunci yang dapat diklaim.',
        'warning',
      );
      return;
    }

    const mediaIds: number[] = filesToRedeem
      .map((file) => this.getFileIntegerId(file))
      .filter((id): id is number => id != null && !isNaN(id) && id > 0);

    if (mediaIds.length === 0) {
      Swal.fire(
        'Peringatan',
        'Pilih minimal satu foto valid untuk diklaim.',
        'warning',
      );
      return;
    }

    if (this.softFileRemaining < mediaIds.length) {
      Swal.fire({
        title: 'Limit Tidak Cukup',
        text: `Limit klaim Anda (${this.softFileRemaining}) kurang dari jumlah foto terkunci (${mediaIds.length}). Silakan klaim sesuai limit atau beli paket tambahan.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Klaim Sesuai Limit',
        cancelButtonText: 'Beli Paket Tambahan',
      }).then((res) => {
        if (res.isConfirmed) {
          this.redeemPhotosLimit();
        } else if (res.dismiss === Swal.DismissReason.cancel) {
          this.openCheckout('all');
        }
      });
      return;
    }

    this.isRedeeming = true;
    const segment = this.route.snapshot.paramMap.get('segment') || pin;
    const url = `/public/orders/${encodeURIComponent(segment)}/soft-files/redeem`;

    this.apiService.post<any>(url, { media_ids: mediaIds }).subscribe({
      next: (res) => {
        this.isRedeeming = false;
        if (res && res.success !== false) {
          this.hasAutoRedeemed = true;

          // Optimistically unlock files locally in memory
          filesToRedeem.forEach((f: any) => {
            f.is_redeemed = true;
            f.is_downloadable = true;
          });

          Swal.fire({
            icon: 'success',
            title: 'Berhasil Klaim',
            text: 'Foto terpilih berhasil diklaim! Sekarang Anda bisa mengunduh file aslinya.',
            timer: 3000,
            timerProgressBar: true,
          }).then(() => {
            window.location.reload();
          });
          const currentSeg = this.route.snapshot.paramMap.get('segment');
          if (currentSeg) this.fetchData(currentSeg);
        } else {
          Swal.fire('Gagal', res?.message || 'Gagal redeem foto', 'error');
        }
      },
      error: (err) => {
        this.isRedeeming = false;
        console.error('[OrderDetail] Error redeeming photos:', err);
        Swal.fire(
          'Gagal',
          err?.error?.message || 'Gagal mengklaim foto. Silakan coba lagi.',
          'error',
        );
      },
    });
  }

  downloadAuthorizedFile(file: any): void {
    if (!this.isFileAuthorized(file)) {
      this.openCheckout('single', file);
      return;
    }
    this.downloadPhoto(
      file.download_url || file.original_url || file.url,
      file.name || 'soft-file.jpg',
    );
  }

  openTransactionModal(): void {
    const segment =
      this.route.snapshot.paramMap.get('segment') || this.currentSegment;
    if (segment) {
      this.fetchData(segment);
    }
    this.isTransactionModalOpen = true;
  }
  closeTransactionModal(): void {
    this.isTransactionModalOpen = false;
  }

  downloadReceiptPdf(): void {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = 600;

    // Calculate dynamic items height to determine canvas height
    const mainItems =
      this.mainPurchasedItems.length > 0
        ? this.mainPurchasedItems
        : [
            {
              product_name: 'Paket Foto',
              product_price: this.overallGrandTotal,
            },
          ];
    const addonItems = this.purchasedAddonItems || [];

    const mainItemsHeight = mainItems.length * 55;
    const addonItemsHeight =
      addonItems.length > 0 ? 35 + addonItems.length * 50 : 0;
    const totalItemsHeight = mainItemsHeight + addonItemsHeight;

    const line2Y = 320 + totalItemsHeight + 20;
    const paySectionY = line2Y + 45;
    const line3Y = paySectionY + 160;
    const grandSectionY = line3Y + 45;

    // Dynamic height ensures no overflow or truncated PDF content
    const canvasHeight = Math.max(900, grandSectionY + 120);
    canvas.width = width;
    canvas.height = canvasHeight;

    // Background: Clean off-white #ffffff
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, canvasHeight);

    const logoImg = new Image();
    const paidImg = new Image();

    logoImg.crossOrigin = 'anonymous';
    paidImg.crossOrigin = 'anonymous';

    logoImg.src = '/assets/queue-monitoring/logo_fotokapsul.svg';
    paidImg.src = '/byul/snappy_paid.png';

    let loadedCount = 0;
    const renderAndSave = () => {
      loadedCount++;
      if (loadedCount < 2) return;

      // 1. Logo at Top Center
      if (logoImg.complete && logoImg.naturalWidth > 0) {
        const logoW = 200;
        const logoH = (logoImg.naturalHeight / logoImg.naturalWidth) * logoW;
        ctx.drawImage(logoImg, (width - logoW) / 2, 35, logoW, logoH);
      } else {
        ctx.fillStyle = '#0047cc';
        ctx.font = 'bold 28px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Fotokapsul', width / 2, 65);
      }

      // 2. Title: Nota Pesanan
      ctx.fillStyle = '#000000';
      ctx.font = 'bold 22px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Nota Pesanan', width / 2, 135);

      // 3. Metadata (No. Order & Date & Time)
      ctx.font = 'bold 16px sans-serif';
      ctx.fillStyle = '#000000';

      // Left: No. Order
      ctx.textAlign = 'left';
      ctx.fillText('No. Order', 50, 190);
      ctx.font = '500 16px sans-serif';
      const orderNo =
        this.order?.invoice ||
        this.sessionData?.invoice ||
        this.order?.order_pin ||
        this.order?.id ||
        '-';
      ctx.fillText(String(orderNo), 50, 215);

      // Right: Date & Time
      ctx.textAlign = 'right';
      ctx.font = 'bold 16px sans-serif';
      ctx.fillText('Date & Time', width - 50, 190);
      ctx.font = '500 16px sans-serif';
      const dateObj = this.transactionTime
        ? new Date(this.transactionTime)
        : new Date();
      const formattedDate =
        dateObj.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: '2-digit',
        }) +
        ' ' +
        dateObj.toLocaleTimeString('id-ID', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }) +
        ' WIB';
      ctx.fillText(formattedDate, width - 50, 215);

      // Divider Line 1
      ctx.strokeStyle = '#d1d5db';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(40, 245);
      ctx.lineTo(width - 40, 245);
      ctx.stroke();

      // 4. Middle Section Background Watermark (Subtle opacity so text is 100% crisp)
      if (paidImg.complete && paidImg.naturalWidth > 0) {
        ctx.save();
        ctx.globalAlpha = 0.12;
        const paidW = 320;
        const paidH = (paidImg.naturalHeight / paidImg.naturalWidth) * paidW;
        ctx.drawImage(paidImg, (width - paidW) / 2, 260, paidW, paidH);
        ctx.restore();
      }

      ctx.textAlign = 'center';
      ctx.fillStyle = '#000000';
      ctx.font = 'bold 20px sans-serif';

      const locationName =
        this.sessionData?.device?.name || this.order?.name || 'SELKACHI CIWALK';
      ctx.fillText(`Photobooth ${locationName}`, width / 2, 285);

      // Main Items List
      let currentY = 330;
      mainItems.forEach((item: any) => {
        const name = this.formatProductName(
          item.product_name || item.details?.product_name || 'Paket Foto',
        );
        const priceStr = this.formatPrice(
          item.product_price ?? item.sub_total ?? 0,
        );

        ctx.font = '500 17px sans-serif';
        ctx.fillStyle = '#111827';
        ctx.fillText(name, width / 2, currentY);

        ctx.font = 'bold 18px sans-serif';
        ctx.fillStyle = '#000000';
        ctx.fillText(priceStr, width / 2, currentY + 26);
        currentY += 55;
      });

      // Add-on Items List (if present)
      if (addonItems.length > 0) {
        currentY += 10;
        ctx.font = 'bold 15px sans-serif';
        ctx.fillStyle = '#4b5563';
        ctx.fillText('Pembelian Add-on / Tambahan:', width / 2, currentY);
        currentY += 30;

        addonItems.forEach((addon: any) => {
          const name = this.formatProductName(
            addon.product_name || addon.name || 'Add-on',
          );
          const priceStr = this.formatPrice(
            addon.sub_total || addon.product_price || addon.total || 0,
          );
          ctx.font = '500 16px sans-serif';
          ctx.fillStyle = '#1f2937';
          ctx.fillText(`Add-on: ${name}`, width / 2, currentY);

          ctx.font = 'bold 17px sans-serif';
          ctx.fillStyle = '#000000';
          ctx.fillText(priceStr, width / 2, currentY + 24);
          currentY += 50;
        });
      }

      // Divider Line 2
      ctx.strokeStyle = '#d1d5db';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(40, line2Y);
      ctx.lineTo(width - 40, line2Y);
      ctx.stroke();

      // 5. Payment Method Section (Clear & Explicit)
      ctx.textAlign = 'center';
      ctx.font = 'bold 18px sans-serif';
      ctx.fillStyle = '#000000';
      ctx.fillText('Payment Method', width / 2, paySectionY);

      const payMethod =
        this.transactionData?.payment_method ||
        this.order?.payment_method ||
        this.sessionData?.order?.payment_method ||
        this.sessionData?.payment_method ||
        this.sessionData?.payment_type ||
        'QRIS';

      ctx.font = 'bold 17px sans-serif';
      ctx.fillStyle = '#111827';
      ctx.fillText(
        String(payMethod).toUpperCase(),
        width / 2,
        paySectionY + 30,
      );

      // Draw Byul character stamp in Payment Method section if available
      if (paidImg.complete && paidImg.naturalWidth > 0) {
        ctx.save();
        ctx.globalAlpha = 0.85;
        const charW = 100;
        const charH = (paidImg.naturalHeight / paidImg.naturalWidth) * charW;
        ctx.drawImage(
          paidImg,
          (width - charW) / 2,
          paySectionY + 45,
          charW,
          charH,
        );
        ctx.restore();
      }

      // Divider Line 3
      ctx.strokeStyle = '#d1d5db';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(40, line3Y);
      ctx.lineTo(width - 40, line3Y);
      ctx.stroke();

      // 6. Grand Total Section
      ctx.textAlign = 'center';
      ctx.font = 'bold 19px sans-serif';
      ctx.fillStyle = '#000000';
      ctx.fillText('Grand Total', width / 2, grandSectionY);

      const grandTotalStr = this.formatPrice(this.overallGrandTotal || 0);
      ctx.font = 'bold 24px sans-serif';
      ctx.fillStyle = '#000000';
      ctx.fillText(grandTotalStr, width / 2, grandSectionY + 38);

      // Generate PDF dynamically sized to fit full content height
      const imgData = canvas.toDataURL('image/png');
      const pdfHeightMm = (canvasHeight / width) * 120;
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [120, pdfHeightMm],
      });
      pdf.addImage(imgData, 'PNG', 0, 0, 120, pdfHeightMm);
      pdf.save(`Nota-Pesanan-${orderNo}.pdf`);
    };

    logoImg.onload = renderAndSave;
    logoImg.onerror = renderAndSave;
    paidImg.onload = renderAndSave;
    paidImg.onerror = renderAndSave;
  }
  printQr(): void {
    window.print();
  }

  ngOnDestroy(): void {
    if (this.mqttClient) this.mqttClient.end();
    if (this.paymentInterval) clearInterval(this.paymentInterval);
    document.body.style.overflow = '';
  }

  // New state variables for navigation
  currentPhotoIndex: number = 0;
  currentFramedIndex: number = 0;
  touchStartX = 0;
  touchEndX = 0;

  handleTouchStart(event: TouchEvent) {
    this.touchStartX = event.changedTouches[0].screenX;
  }

  handleTouchEnd(event: TouchEvent, isFramed: boolean = false) {
    this.touchEndX = event.changedTouches[0].screenX;
    this.handleSwipe(isFramed);
  }

  private handleSwipe(isFramed: boolean) {
    const swipeThreshold = 50;
    const mockEvent = new Event('swipe');
    if (this.touchEndX < this.touchStartX - swipeThreshold) {
      isFramed ? this.nextFramedPhoto(mockEvent) : this.nextPhoto(mockEvent);
    }
    if (this.touchEndX > this.touchStartX + swipeThreshold) {
      isFramed ? this.prevFramedPhoto(mockEvent) : this.prevPhoto(mockEvent);
    }
  }

  // Open full-screen photo modal
  openModal(photo: any) {
    this.selectedFile = photo;
    this.selectedPhotoUrl = photo.url;
    this.selectedPhotoName = photo.name || 'soft_file.jpg';
    this.currentPhotoIndex = this.softFiles.findIndex((f) => f.id === photo.id);
    if (this.currentPhotoIndex === -1) this.currentPhotoIndex = 0;
    this.isModalOpen = true;
    document.body.style.overflow = 'hidden';
  }

  nextPhoto(event: Event) {
    event.stopPropagation();
    if (this.softFiles.length > 1) {
      this.currentPhotoIndex =
        (this.currentPhotoIndex + 1) % this.softFiles.length;
      this.updateSelectedPhoto();
    }
  }

  prevPhoto(event: Event) {
    event.stopPropagation();
    if (this.softFiles.length > 1) {
      this.currentPhotoIndex =
        (this.currentPhotoIndex - 1 + this.softFiles.length) %
        this.softFiles.length;
      this.updateSelectedPhoto();
    }
  }

  private updateSelectedPhoto() {
    const photo = this.softFiles[this.currentPhotoIndex];
    this.selectedFile = photo;
    this.selectedPhotoUrl = photo.url;
    this.selectedPhotoName = photo.name || 'soft_file.jpg';
  }

  // Close full-screen photo modal
  closeModal() {
    this.isModalOpen = false;
    this.selectedPhotoUrl = '';
    this.selectedPhotoName = '';
    document.body.style.overflow = '';
  }

  // Open framed photo modal (always unlocked)
  openFramedModal(url: string, name: string) {
    this.selectedFramedUrl = url;
    this.selectedFramedName = name;
    this.currentFramedIndex = this.framedPhotos.findIndex((f) => f.url === url);
    if (this.currentFramedIndex === -1) this.currentFramedIndex = 0;
    this.isFramedModalOpen = true;
    document.body.style.overflow = 'hidden';
  }

  nextFramedPhoto(event: Event) {
    event.stopPropagation();
    if (this.framedPhotos.length > 1) {
      this.currentFramedIndex =
        (this.currentFramedIndex + 1) % this.framedPhotos.length;
      this.updateSelectedFramedPhoto();
    }
  }

  prevFramedPhoto(event: Event) {
    event.stopPropagation();
    if (this.framedPhotos.length > 1) {
      this.currentFramedIndex =
        (this.currentFramedIndex - 1 + this.framedPhotos.length) %
        this.framedPhotos.length;
      this.updateSelectedFramedPhoto();
    }
  }

  private updateSelectedFramedPhoto() {
    const photo = this.framedPhotos[this.currentFramedIndex];
    if (photo) {
      this.selectedFramedUrl = photo.url;
      this.selectedFramedName = photo.name || 'framed_photo.jpg';
    }
  }

  // Close framed photo modal
  closeFramedModal() {
    this.isFramedModalOpen = false;
    this.selectedFramedUrl = '';
    this.selectedFramedName = '';
    document.body.style.overflow = '';
  }

  // Toggle FAQ Accordion panel
  toggleFaq(index: number) {
    this.activeFaqIndex = this.activeFaqIndex === index ? null : index;
  }

  private getCorsFreeUrl(url: string): string {
    if (!url) return url;
    if (
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1')
    ) {
      if (url.includes('pb-storage.temporary.my.id')) {
        return url.replace('https://pb-storage.temporary.my.id', '/pb-storage');
      }
    }
    return url;
  }

  public isIosDevice(): boolean {
    if (typeof window === 'undefined' || typeof navigator === 'undefined')
      return false;
    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
  }

  private fetchWithTimeout(
    url: string,
    timeoutMs: number = 5000,
    headers: Record<string, string> = {},
  ): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    return fetch(url, {
      signal: controller.signal,
      cache: 'no-cache'
    }).finally(() => clearTimeout(timeoutId));
  }

  private async fetchVideoBlob(fileUrl: string): Promise<Blob> {
    if (!fileUrl) throw new Error('Empty URL');
    let cleanUrl = fileUrl.trim();
    if (typeof window !== 'undefined' && window.location.protocol === 'https:' && cleanUrl.startsWith('http://')) {
      cleanUrl = cleanUrl.replace('http://', 'https://');
    }

    const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    // 0. On local environment (localhost / 127.0.0.1), use Angular Dev Server proxy first
    if (isLocalhost) {
      let localProxyUrl = '';
      if (cleanUrl.includes('pb-storage.temporary.my.id')) localProxyUrl = cleanUrl.replace('https://pb-storage.temporary.my.id', '/pb-storage');
      else if (cleanUrl.includes('sos3io-1.neoid.com')) localProxyUrl = cleanUrl.replace('https://sos3io-1.neoid.com', '/neoid-storage');
      else if (cleanUrl.includes('photobooth-retail.api.dev.awh.co.id')) localProxyUrl = cleanUrl.replace('https://photobooth-retail.api.dev.awh.co.id', '/pb-api-dev');
      else if (cleanUrl.includes('photobooth-retail.api.awh.co.id')) localProxyUrl = cleanUrl.replace('https://photobooth-retail.api.awh.co.id', '/pb-api-prod');

      if (localProxyUrl) {
        try {
          // Large timeout for videos (30 seconds)
          const res = await this.fetchWithTimeout(localProxyUrl, 30000);
          if (res.ok) {
            const blob = await res.blob();
            if (blob && blob.size > 0) return blob;
          }
        } catch (e) {
          // continue to fallbacks
        }
      }
    }

    // 1. Direct fetch with massive timeout (30 seconds) because videos are large!
    try {
      const res = await this.fetchWithTimeout(cleanUrl, 30000);
      if (res.ok) {
        const contentType = res.headers.get('content-type') || '';
        if (!contentType.toLowerCase().includes('text/html')) {
          const blob = await res.blob();
          if (blob && blob.size > 0) return blob;
        }
      }
    } catch (e) {
      console.warn('Direct fetch failed for video, trying fallback proxy', e);
    }

    // 2. Reliable CORS proxy for videos (corsproxy.io) - wsrv.nl will corrupt video files!
    try {
      const proxyUrl = `https://corsproxy.io/?${encodeURIComponent(cleanUrl)}`;
      const res = await this.fetchWithTimeout(proxyUrl, 30000);
      if (res.ok) {
        const contentType = res.headers.get('content-type') || '';
        if (!contentType.toLowerCase().includes('text/html')) {
          const blob = await res.blob();
          if (blob && blob.size > 0) return blob;
        }
      }
    } catch (e) {}

    throw new Error(`Failed to fetch video blob for ${cleanUrl}`);
  }

  private loadImageViaCanvas(
    url: string,
    useCrossOrigin = true,
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      if (useCrossOrigin) {
        img.crossOrigin = 'anonymous';
      }
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth || img.width;
          canvas.height = img.naturalHeight || img.height;
          const ctx = canvas.getContext('2d');
          if (!ctx) return reject('No canvas context');
          ctx.drawImage(img, 0, 0);
          canvas.toBlob(
            (blob) => {
              if (blob && blob.size > 0) resolve(blob);
              else reject('Canvas toBlob returned empty blob');
            },
            'image/jpeg',
            0.95,
          );
        } catch (e) {
          reject(e);
        }
      };
      img.onerror = (err) => reject(err);
      img.src = url;
    });
  }

  private convertImageToBlobViaDataUrl(url: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth || img.width || 800;
          canvas.height = img.naturalHeight || img.height || 1200;
          const ctx = canvas.getContext('2d');
          if (!ctx) return reject('No canvas ctx');
          ctx.drawImage(img, 0, 0);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
          fetch(dataUrl, { cache: 'no-cache' })
            .then((res) => res.blob())
            .then((blob) => resolve(blob))
            .catch((err) => reject(err));
        } catch (e) {
          reject(e);
        }
      };
      img.onerror = (e) => reject(e);
      img.src = url;
    });
  }

  private async fetchFastFileBlob(fileUrl: string): Promise<Blob> {
    if (!fileUrl) throw new Error('Empty URL');

    if (fileUrl.startsWith('data:')) {
      const res = await fetch(fileUrl, { cache: 'no-cache' });
      return await res.blob();
    }

    let cleanUrl = fileUrl.trim();
    if (
      typeof window !== 'undefined' &&
      window.location.protocol === 'https:' &&
      cleanUrl.startsWith('http://')
    ) {
      cleanUrl = cleanUrl.replace('http://', 'https://');
    }

    const isLocalhost =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1');

    // 0. On local environment (localhost / 127.0.0.1), use Angular Dev Server proxy first
    if (isLocalhost) {
      let localProxyUrl = '';
      if (cleanUrl.includes('pb-storage.temporary.my.id')) {
        localProxyUrl = cleanUrl.replace(
          'https://pb-storage.temporary.my.id',
          '/pb-storage',
        );
      } else if (cleanUrl.includes('sos3io-1.neoid.com')) {
        localProxyUrl = cleanUrl.replace(
          'https://sos3io-1.neoid.com',
          '/neoid-storage',
        );
      } else if (cleanUrl.includes('photobooth-retail.api.dev.awh.co.id')) {
        localProxyUrl = cleanUrl.replace(
          'https://photobooth-retail.api.dev.awh.co.id',
          '/pb-api-dev',
        );
      } else if (cleanUrl.includes('photobooth-retail.api.awh.co.id')) {
        localProxyUrl = cleanUrl.replace(
          'https://photobooth-retail.api.awh.co.id',
          '/pb-api-prod',
        );
      }

      if (localProxyUrl) {
        try {
          const res = await this.fetchWithTimeout(localProxyUrl, 5000);
          if (res.ok) {
            const blob = await res.blob();
            if (blob && blob.size > 0) return blob;
          }
        } catch (e) {
          // continue to fallbacks
        }
      }
    }

    // 1. Direct clean URL fetch (without custom headers to avoid CORS preflight options failure)
    try {
      const res = await this.fetchWithTimeout(cleanUrl, 3500);
      if (res.ok) {
        const contentType = res.headers.get('content-type') || '';
        if (!contentType.toLowerCase().includes('text/html')) {
          const blob = await res.blob();
          if (blob && blob.size > 0) return blob;
        }
      }
    } catch (e) {
      // Continue
    }

    // 2. High-performance image proxies (wsrv.nl / images.weserv.nl / corsproxy / allorigins / codetabs)
    const proxyUrls: string[] = [
      `https://wsrv.nl/?url=${encodeURIComponent(cleanUrl)}`,
      `https://images.weserv.nl/?url=${encodeURIComponent(cleanUrl)}`,
      `https://corsproxy.io/?${encodeURIComponent(cleanUrl)}`,
      `https://api.allorigins.win/raw?url=${encodeURIComponent(cleanUrl)}`,
      `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(cleanUrl)}`,
    ];

    for (const proxyUrl of proxyUrls) {
      try {
        const res = await this.fetchWithTimeout(proxyUrl, 5000);
        if (res.ok) {
          const contentType = res.headers.get('content-type') || '';
          if (!contentType.toLowerCase().includes('text/html')) {
            const blob = await res.blob();
            if (blob && blob.size > 0) return blob;
          }
        }
      } catch (e) {
        // continue to next proxy
      }
    }

    // 3. Try Canvas load with crossOrigin = 'anonymous'
    try {
      const canvasBlob = await Promise.race([
        this.loadImageViaCanvas(cleanUrl, true),
        new Promise<Blob>((_, reject) =>
          setTimeout(() => reject(new Error('Canvas timeout')), 3000),
        ),
      ]);
      if (canvasBlob && canvasBlob.size > 0) return canvasBlob;
    } catch (e) {
      // Continue
    }

    // 4. Try Data URL conversion via Canvas
    try {
      const dataBlob = await Promise.race([
        this.convertImageToBlobViaDataUrl(cleanUrl),
        new Promise<Blob>((_, reject) =>
          setTimeout(() => reject(new Error('DataUrl timeout')), 3000),
        ),
      ]);
      if (dataBlob && dataBlob.size > 0) return dataBlob;
    } catch (e) {
      // continue
    }

    throw new Error(`Failed to fetch blob for ${cleanUrl}`);
  }

  // Download photo safely
  downloadPhoto(url: string, filename: string) {
    this.isDownloading = true;
    this.downloadProgress = 30;
    this.downloadCompletedCount = 0;
    this.downloadTotalCount = 1;
    this.downloadStatusText = `Mengunduh ${filename}...`;

    this.fetchFastFileBlob(url)
      .then((blob) => {
        this.downloadProgress = 100;
        this.downloadCompletedCount = 1;
        this.downloadStatusText = 'Menyimpan ke perangkat...';
        this.triggerBlobDownload(blob, filename);
      })
      .catch((error) => {
        console.error(
          'Blob fetch failed, triggering direct native download fallback',
          error,
        );
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      })
      .finally(() => {
        setTimeout(() => {
          this.isDownloading = false;
        }, 800);
      });
  }

  downloadVideo(url: string, filename: string) {
    this.isDownloading = true;
    this.downloadProgress = 30;
    this.downloadCompletedCount = 0;
    this.downloadTotalCount = 1;
    this.downloadStatusText = `Mengunduh ${filename}...`;

    this.fetchWithTimeout(url, 30000)
      .then((res) => {
        if (!res.ok) throw new Error('Network response was not ok');
        return res.blob();
      })
      .then((blob) => {
        this.downloadProgress = 100;
        this.downloadCompletedCount = 1;
        this.downloadStatusText = 'Menyimpan ke perangkat...';
        this.triggerBlobDownload(blob, filename);
      })
      .catch((error) => {
        console.error('Video blob fetch failed, triggering direct native download fallback', error);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.setAttribute('download', filename);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      })
      .finally(() => {
        setTimeout(() => {
          this.isDownloading = false;
        }, 800);
      });
  }

  private downloadForIos(blob: Blob, filename: string) {
    this.triggerBlobDownload(blob, filename);
  }

  private triggerBlobDownload(blob: Blob, filename: string) {
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => {
      window.URL.revokeObjectURL(blobUrl);
    }, 10000);
  }

  // Fallback direct download via SAME-ORIGIN blob URLs to prevent browser from opening preview tabs
  private async downloadFilesDirectlyFallback(
    files: { url: string; name: string }[],
  ): Promise<void> {
    for (let index = 0; index < files.length; index++) {
      const file = files[index];
      try {
        const blob = await this.fetchFastFileBlob(file.url);
        this.triggerBlobDownload(blob, file.name);
      } catch (err) {
        const a = document.createElement('a');
        a.href = file.url;
        a.download = file.name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      await new Promise((r) => setTimeout(r, 400));
    }

    this.isDownloading = false;
  }

  private async downloadZipFiles(
    files: { url: string; name: string }[],
    zipFilename: string,
  ) {
    if (!files || files.length === 0) return;

    // Ensure unique filenames for JSZip entry so no photo overwrites another
    const nameMap: { [key: string]: number } = {};
    const sanitizedFiles = files.map((file, idx) => {
      let baseName = file.name || `photo_${idx + 1}.jpg`;
      if (nameMap[baseName]) {
        nameMap[baseName]++;
        const lastDot = baseName.lastIndexOf('.');
        if (lastDot > 0) {
          baseName = `${baseName.substring(0, lastDot)}_${nameMap[baseName]}${baseName.substring(lastDot)}`;
        } else {
          baseName = `${baseName}_${nameMap[baseName]}`;
        }
      } else {
        nameMap[baseName] = 1;
      }
      return { url: file.url, name: baseName };
    });

    this.isDownloading = true;
    this.downloadProgress = 10;
    this.downloadCompletedCount = 0;
    this.downloadTotalCount = sanitizedFiles.length;
    this.downloadStatusText = 'Menyiapkan unduhan...';

    const zip = new JSZip();
    let successCount = 0;
    let failCount = 0;
    const failedFiles: string[] = [];

    // Sequential fetching with try-catch to check download status of each file
    for (const file of sanitizedFiles) {
      try {
        const blob = await this.fetchFastFileBlob(file.url);
        zip.file(file.name, blob);
        successCount++;
      } catch (err: any) {
        console.warn(
          `Gagal mengunduh foto '${file.name}' (kendala CORS / Network):`,
          err,
        );
        failCount++;
        failedFiles.push(file.name);
      } finally {
        this.downloadCompletedCount++;
        this.downloadProgress = Math.min(
          85,
          Math.round(
            (this.downloadCompletedCount / sanitizedFiles.length) * 75,
          ) + 10,
        );
        this.downloadStatusText = `Memproses foto ${this.downloadCompletedCount} dari ${sanitizedFiles.length}...`;
      }
    }

    // If at least 1 file was fetched into zip
    if (successCount > 0) {
      this.downloadStatusText = 'Mengompresi file ke ZIP...';
      this.downloadProgress = 90;

      try {
        const zipBlob = await zip.generateAsync({
          type: 'blob',
          mimeType: 'application/zip',
          compression: 'STORE',
        });

        this.downloadStatusText = 'Menyimpan file ZIP...';
        this.downloadProgress = 100;

        this.triggerBlobDownload(zipBlob, zipFilename);

        setTimeout(() => {
          this.isDownloading = false;

          // Notification if some files failed due to CORS or network errors
          if (failCount > 0) {
            Swal.fire({
              icon: 'warning',
              title: 'Unduhan Selesai dengan Catatan',
              html: `
                <div style="text-align: left; font-size: 14px; line-height: 1.6;">
                  <p style="margin-bottom: 8px;">Beberapa foto tidak dapat diunduh karena kendala <b>CORS / Koneksi</b>:</p>
                  <ul style="margin-bottom: 12px; padding-left: 20px;">
                    <li><b style="color: #2e7d32;">✔ ${successCount}</b> foto berhasil dimasukkan ke ZIP.</li>
                    <li><b style="color: #d32f2f;">✖ ${failCount}</b> foto gagal diunduh (terkendala CORS).</li>
                  </ul>
                  <small style="color: #666;">File ZIP berisi <b>${successCount}</b> foto yang berhasil diunduh telah tersimpan.</small>
                </div>
              `,
              confirmButtonText: 'Mengerti',
              confirmButtonColor: '#3085d6',
            });
          }
        }, 800);
        return;
      } catch (e) {
        console.error('ZIP creation error, falling back to direct download', e);
      }
    }

    // Fallback: If ALL files failed (0 files succeeded)
    this.isDownloading = false;
    Swal.fire({
      icon: 'error',
      title: 'Gagal Mengunduh Foto',
      html: `
        <div style="text-align: left; font-size: 14px; line-height: 1.6;">
          <p>Seluruh <b>${sanitizedFiles.length}</b> foto gagal diunduh.</p>
          <p style="color: #d32f2f; margin-top: 6px;">Semua foto terhalang kendala keamanan <b>CORS</b> server asal atau koneksi terputus.</p>
        </div>
      `,
      confirmButtonText: 'Tutup',
      confirmButtonColor: '#d32f2f',
    });
  }

  downloadAllSoftFiles() {
    if (this.authorizedSoftFiles.length === 0) return;
    const filesToDownload = this.authorizedSoftFiles
      .map((file: any, index: number) => ({
        url:
          file.download_url || file.original_url || file.url || file.path || '',
        name: file.name || `soft_file_${index + 1}.jpg`,
      }))
      .filter((f) => f.url && f.url.trim().length > 0);

    if (filesToDownload.length === 0) return;
    this.downloadZipFiles(filesToDownload, 'soft_files.zip');
  }

  downloadAllFramedPhotos() {
    if (this.framedPhotos.length === 0) return;
    const filesToDownload = this.framedPhotos
      .map((photo: any, index: number) => ({
        url:
          photo.url ||
          photo.download_url ||
          photo.original_url ||
          photo.path ||
          (typeof photo === 'string' ? photo : ''),
        name: photo.name || `framed_photo_${index + 1}.jpg`,
      }))
      .filter((f) => f.url && f.url.trim().length > 0);

    if (filesToDownload.length === 0) return;
    this.downloadZipFiles(filesToDownload, 'framed_photos.zip');
  }

  downloadAllAiPhotos() {
    if (this.aiPhotos.length === 0) return;
    const filesToDownload = this.aiPhotos
      .map((photo: any, index: number) => ({
        url:
          photo.url ||
          photo.download_url ||
          photo.original_url ||
          photo.path ||
          (typeof photo === 'string' ? photo : ''),
        name: photo.name || `ai_photo_${index + 1}.jpg`,
      }))
      .filter((f) => f.url && f.url.trim().length > 0);

    if (filesToDownload.length === 0) return;
    this.downloadZipFiles(filesToDownload, 'ai_photos.zip');
  }

  downloadAllFotoinAja() {
    if (this.fotoinAjaMatches.length === 0) return;
    const filesToDownload = this.fotoinAjaMatches
      .map((photo: any, index: number) => ({
        url: photo.url || photo.matched_file,
        name: photo.name || `fotoin_aja_${index + 1}.jpg`,
      }))
      .filter((f) => f.url && f.url.trim().length > 0);

    if (filesToDownload.length === 0) return;
    this.downloadZipFiles(filesToDownload, 'fotoin_aja.zip');
  }

  downloadAllMoments() {
    if (!this.sessionData) return;
    const filesToDownload: { url: string; name: string }[] = [];

    if (this.framedPhotos.length > 0) {
      this.framedPhotos.forEach((photo: any, index: number) => {
        const fileUrl =
          photo.url ||
          photo.download_url ||
          photo.original_url ||
          photo.path ||
          (typeof photo === 'string' ? photo : '');
        if (fileUrl) {
          const rawName =
            photo.name && !photo.name.startsWith('photo_')
              ? photo.name
              : `framed_photo_${index + 1}.jpg`;
          filesToDownload.push({
            url: fileUrl,
            name: rawName.startsWith('framed_') ? rawName : `framed_${rawName}`,
          });
        }
      });
    }

    if (this.authorizedSoftFiles.length > 0) {
      this.authorizedSoftFiles.forEach((file: any, index: number) => {
        const fileUrl =
          file.download_url ||
          file.original_url ||
          file.url ||
          file.path ||
          (typeof file === 'string' ? file : '');
        if (fileUrl) {
          const rawName =
            file.name && !file.name.startsWith('photo_')
              ? file.name
              : `soft_file_${index + 1}.jpg`;
          filesToDownload.push({
            url: fileUrl,
            name: rawName.startsWith('soft_') ? rawName : `soft_${rawName}`,
          });
        }
      });
    }

    if (this.aiPhotos.length > 0) {
      this.aiPhotos.forEach((file: any, index: number) => {
        const fileUrl =
          file.download_url ||
          file.original_url ||
          file.url ||
          file.path ||
          (typeof file === 'string' ? file : '');
        if (fileUrl) {
          const rawName =
            file.name && !file.name.startsWith('photo_')
              ? file.name
              : `ai_photo_${index + 1}.jpg`;
          filesToDownload.push({
            url: fileUrl,
            name: rawName.startsWith('ai_') ? rawName : `ai_${rawName}`,
          });
        }
      });
    }

    if (this.fotoinAjaMatches.length > 0) {
      this.fotoinAjaMatches.forEach((file: any, index: number) => {
        const fileUrl = file.url || file.matched_file || '';
        if (fileUrl) {
          const rawName = file.name || `fotoin_aja_${index + 1}.jpg`;
          filesToDownload.push({
            url: fileUrl,
            name: rawName.startsWith('fotoin_aja_')
              ? rawName
              : `fotoin_aja_${rawName}`,
          });
        }
      });
    }

    if (filesToDownload.length === 0) return;
    this.downloadZipFiles(filesToDownload, 'all_photos.zip');
  }
}
